// External/utility imports
// Utility functions for the Bluey Uno game
/**
 * Shuffles an array using the Fisher-Yates algorithm
 * @param {Array} array - The array to shuffle
 * @returns {Array} A new shuffled array
 */ function $820877bea407f5fe$export$7ac3a88d34a76e7(array) {
    const shuffled = [
        ...array
    ];
    for(let i = shuffled.length - 1; i > 0; i--){
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [
            shuffled[j],
            shuffled[i]
        ];
    }
    return shuffled;
}
function $820877bea407f5fe$export$41428deff1a0db9e(element, styles) {
    for (const [property, value] of Object.entries(styles))element.style[property] = value;
    return element;
}
function $820877bea407f5fe$export$c8a8987d4410bf2d(tagName, attributes = {}, styles = {}, content = null) {
    const element = document.createElement(tagName);
    // Set attributes
    Object.entries(attributes).forEach(([key, value])=>{
        if (key === 'className') element.className = value;
        else element.setAttribute(key, value);
    });
    // Apply styles
    $820877bea407f5fe$export$41428deff1a0db9e(element, styles);
    // Add content
    if (content) {
        if (typeof content === 'string') element.textContent = content;
        else element.appendChild(content);
    }
    return element;
}
function $820877bea407f5fe$export$62dd595d6a764af5(currentIndex, direction, totalPlayers) {
    let nextIndex = (currentIndex + direction) % totalPlayers;
    if (nextIndex < 0) nextIndex += totalPlayers;
    return nextIndex;
}
function $820877bea407f5fe$export$aaf39ef296ada06c(message, level = 'log') {
    const prefix = '[Bluey Uno] ';
    switch(level){
        case 'warn':
            console.warn(prefix + message);
            break;
        case 'error':
            console.error(prefix + message);
            break;
        case 'info':
            console.info(prefix + message);
            break;
        default:
            console.log(prefix + message);
    }
}
function $820877bea407f5fe$export$1391212d75b2ee65(ms) {
    return new Promise((resolve)=>setTimeout(resolve, ms));
}


// Simple audio system for game sounds
class $b7cfe35c464a2c84$var$SoundSystem {
    constructor(){
        this.sounds = {};
        this.enabled = true;
        this.initialized = false;
    }
    // Initialize the sound system with various game sounds
    initialize() {
        if (this.initialized) return;
        // Create audio context
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            // Create sounds
            this.createCardPlaySound();
            this.createYourTurnSound();
            this.createUnoCallSound();
            this.createWinSound();
            this.loadBingoSound();
            this.loadBlueySound();
            this.loadDadSound();
            this.initialized = true;
        } catch (e) {
            console.error("Web Audio API not supported or other audio error:", e);
            this.enabled = false;
        }
    }
    // Create a soft beep for card play
    createCardPlaySound() {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = 440; // A4 note
        gainNode.gain.value = 0.05; // Quiet sound
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        this.sounds.cardPlay = {
            play: ()=>{
                if (!this.enabled) return;
                const osc = this.audioContext.createOscillator();
                const gain = this.audioContext.createGain();
                osc.type = 'sine';
                osc.frequency.value = 440;
                gain.gain.value = 0.05;
                gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.3);
                osc.connect(gain);
                gain.connect(this.audioContext.destination);
                osc.start();
                osc.stop(this.audioContext.currentTime + 0.3);
            }
        };
    }
    // Create a pleasant chime for "your turn"
    createYourTurnSound() {
        this.sounds.yourTurn = {
            play: ()=>{
                if (!this.enabled) return;
                // Play a sequence of two nice tones
                const now = this.audioContext.currentTime;
                // First note
                const osc1 = this.audioContext.createOscillator();
                const gain1 = this.audioContext.createGain();
                osc1.type = 'sine';
                osc1.frequency.value = 523.25; // C5
                gain1.gain.value = 0.1;
                gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
                osc1.connect(gain1);
                gain1.connect(this.audioContext.destination);
                osc1.start(now);
                osc1.stop(now + 0.5);
                // Second note (slightly delayed)
                const osc2 = this.audioContext.createOscillator();
                const gain2 = this.audioContext.createGain();
                osc2.type = 'sine';
                osc2.frequency.value = 659.25; // E5
                gain2.gain.value = 0.1;
                gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
                osc2.connect(gain2);
                gain2.connect(this.audioContext.destination);
                osc2.start(now + 0.2);
                osc2.stop(now + 0.8);
            }
        };
    }
    // Fun sound for calling UNO
    createUnoCallSound() {
        this.sounds.unoCall = {
            play: ()=>{
                if (!this.enabled) return;
                const now = this.audioContext.currentTime;
                // Quick ascending notes
                [
                    440,
                    523.25,
                    659.25,
                    783.99,
                    880
                ].forEach((freq, i)=>{
                    const osc = this.audioContext.createOscillator();
                    const gain = this.audioContext.createGain();
                    osc.type = 'sine';
                    osc.frequency.value = freq;
                    gain.gain.value = 0.1;
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2 + i * 0.1);
                    osc.connect(gain);
                    gain.connect(this.audioContext.destination);
                    osc.start(now + i * 0.08);
                    osc.stop(now + 0.2 + i * 0.1);
                });
            }
        };
    }
    // Victory fanfare when winning
    createWinSound() {
        this.sounds.win = {
            play: ()=>{
                if (!this.enabled) return;
                const now = this.audioContext.currentTime;
                // Fanfare sequence
                [
                    523.25,
                    659.25,
                    783.99,
                    1046.50
                ].forEach((freq, i)=>{
                    const osc = this.audioContext.createOscillator();
                    const gain = this.audioContext.createGain();
                    osc.type = 'sine';
                    osc.frequency.value = freq;
                    gain.gain.value = 0.1;
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5 + i * 0.2);
                    osc.connect(gain);
                    gain.connect(this.audioContext.destination);
                    osc.start(now + i * 0.2);
                    osc.stop(now + 0.5 + i * 0.2);
                });
            }
        };
    }
    // Load the Bingo sound file
    loadBingoSound() {
        // Similar to image handling, use the preloaded audio element
        const preloadedBingoAudio = document.getElementById('bingo-sound-preload');
        this.sounds.bingo = {
            play: ()=>{
                if (!this.enabled) return;
                try {
                    // Use the preloaded audio element if available
                    if (preloadedBingoAudio) {
                        console.log('Using preloaded Bingo sound');
                        preloadedBingoAudio.currentTime = 0;
                        preloadedBingoAudio.volume = 0.5;
                        // Create a clone to allow overlapping sounds
                        const audioClone = preloadedBingoAudio.cloneNode();
                        audioClone.volume = 0.5;
                        // Play the sound
                        audioClone.play().then(()=>{
                            console.log('Bingo sound played successfully!');
                            // Remove clone after it finishes playing to prevent memory leaks
                            audioClone.onended = ()=>{
                                if (audioClone.parentNode) audioClone.parentNode.removeChild(audioClone);
                            };
                        }).catch((err)=>{
                            console.error('Error playing cloned Bingo sound:', err);
                            // Try direct play if clone fails
                            preloadedBingoAudio.play().catch((e)=>{
                                console.error('Error playing original Bingo sound:', e);
                            });
                        });
                    } else {
                        console.log('Preloaded Bingo sound not found, using fallback');
                        const fallbackAudio = new Audio('public/audio/bingo.mp3');
                        fallbackAudio.volume = 0.5;
                        fallbackAudio.play().catch((error)=>{
                            console.error('Fallback Bingo sound failed:', error);
                        });
                    }
                } catch (e) {
                    console.error("Exception playing Bingo sound:", e);
                }
            }
        };
    }
    // Load the Bluey sound file
    loadBlueySound() {
        // Similar to Bingo sound, use the preloaded audio element
        const preloadedBlueyAudio = document.getElementById('bluey-sound-preload');
        this.sounds.bluey = {
            play: ()=>{
                if (!this.enabled) return;
                try {
                    // Use the preloaded audio element if available
                    if (preloadedBlueyAudio) {
                        console.log('Using preloaded Bluey sound');
                        preloadedBlueyAudio.currentTime = 0;
                        preloadedBlueyAudio.volume = 0.5;
                        // Create a clone to allow overlapping sounds
                        const audioClone = preloadedBlueyAudio.cloneNode();
                        audioClone.volume = 0.5;
                        // Play the sound
                        audioClone.play().then(()=>{
                            console.log('Bluey sound played successfully!');
                            // Remove clone after it finishes playing to prevent memory leaks
                            audioClone.onended = ()=>{
                                if (audioClone.parentNode) audioClone.parentNode.removeChild(audioClone);
                            };
                        }).catch((err)=>{
                            console.error('Error playing cloned Bluey sound:', err);
                            // Try direct play if clone fails
                            preloadedBlueyAudio.play().catch((e)=>{
                                console.error('Error playing original Bluey sound:', e);
                            });
                        });
                    } else {
                        console.log('Preloaded Bluey sound not found, using fallback');
                        const fallbackAudio = new Audio('public/audio/bluey.mp3');
                        fallbackAudio.volume = 0.5;
                        fallbackAudio.play().catch((error)=>{
                            console.error('Fallback Bluey sound failed:', error);
                        });
                    }
                } catch (e) {
                    console.error("Exception playing Bluey sound:", e);
                }
            }
        };
    }
    // Load the Dad sound file
    loadDadSound() {
        // Similar to Bingo sound, use the preloaded audio element
        const preloadedDadAudio = document.getElementById('dad-sound-preload');
        this.sounds.dad = {
            play: ()=>{
                if (!this.enabled) return;
                try {
                    // Use the preloaded audio element if available
                    if (preloadedDadAudio) {
                        console.log('Using preloaded Dad sound');
                        preloadedDadAudio.currentTime = 0;
                        preloadedDadAudio.volume = 0.5;
                        // Create a clone to allow overlapping sounds
                        const audioClone = preloadedDadAudio.cloneNode();
                        audioClone.volume = 0.5;
                        // Play the sound
                        audioClone.play().then(()=>{
                            console.log('Dad sound played successfully!');
                            // Remove clone after it finishes playing to prevent memory leaks
                            audioClone.onended = ()=>{
                                if (audioClone.parentNode) audioClone.parentNode.removeChild(audioClone);
                            };
                        }).catch((err)=>{
                            console.error('Error playing cloned Dad sound:', err);
                            // Try direct play if clone fails
                            preloadedDadAudio.play().catch((e)=>{
                                console.error('Error playing original Dad sound:', e);
                            });
                        });
                    } else {
                        console.log('Preloaded Dad sound not found, using fallback');
                        const fallbackAudio = new Audio('public/audio/dad.mp3');
                        fallbackAudio.volume = 0.5;
                        fallbackAudio.play().catch((error)=>{
                            console.error('Fallback Dad sound failed:', error);
                        });
                    }
                } catch (e) {
                    console.error("Exception playing Dad sound:", e);
                }
            }
        };
    }
    // Play a specific sound
    play(soundName) {
        if (!this.initialized) this.initialize();
        if (this.sounds[soundName]) this.sounds[soundName].play();
    }
    // Enable or disable sounds
    toggleSound(enabled) {
        this.enabled = enabled;
    }
}
// Create a singleton instance
const $b7cfe35c464a2c84$var$soundSystem = new $b7cfe35c464a2c84$var$SoundSystem();
var $b7cfe35c464a2c84$export$2e2bcd8739ae039 = $b7cfe35c464a2c84$var$soundSystem;


// Card colors
const $125bb8bfc3b37dfd$export$8f45430ccf837300 = [
    'red',
    'blue',
    'green',
    'yellow'
];
// Card values
const $125bb8bfc3b37dfd$var$numberValues = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9'
];
const $125bb8bfc3b37dfd$var$specialValues = [
    'Skip',
    'Reverse',
    'Draw 2'
];
const $125bb8bfc3b37dfd$var$wildValues = [
    'Wild',
    'Wild Draw 4'
];
// Emoji mapping for cards based on the image
const $125bb8bfc3b37dfd$var$emojiMap = {
    // Numbers - using the emoji faces from the image
    '0': "\uD83D\uDE36",
    '1': "\uD83D\uDE2C",
    '2': "\uD83D\uDE01",
    '3': "\uD83D\uDE0D",
    '4': "\uD83D\uDE07",
    '5': "\uD83D\uDE0E",
    '6': "\uD83D\uDE42",
    '7': "\uD83D\uDE0A",
    '8': "\uD83D\uDE18",
    '9': "\uD83D\uDE20",
    // Special cards based on the image
    'Skip': "\uD83D\uDE45\u200D\u2640\uFE0F",
    'Reverse': "\uD83D\uDC49",
    'Draw 2': "\uD83D\uDC6F\u200D\u2640\uFE0F",
    'Wild': "\uD83D\uDC4D",
    'Wild Draw 4': "\uD83D\uDCA9"
};
// Create a deck of Uno cards
function $125bb8bfc3b37dfd$export$ea1002f91841a9fc() {
    const deck = [];
    // Add number cards
    $125bb8bfc3b37dfd$export$8f45430ccf837300.forEach((color)=>{
        // One 0 card per color
        deck.push({
            color: color,
            value: '0',
            emoji: $125bb8bfc3b37dfd$var$emojiMap['0']
        });
        // Two of each number 1-9 per color
        $125bb8bfc3b37dfd$var$numberValues.slice(1).forEach((value)=>{
            for(let i = 0; i < 2; i++)deck.push({
                color: color,
                value: value,
                emoji: $125bb8bfc3b37dfd$var$emojiMap[value]
            });
        });
        // Two of each special card per color
        $125bb8bfc3b37dfd$var$specialValues.forEach((value)=>{
            for(let i = 0; i < 2; i++)deck.push({
                color: color,
                value: value,
                emoji: $125bb8bfc3b37dfd$var$emojiMap[value]
            });
        });
    });
    // Add wild cards (4 of each)
    $125bb8bfc3b37dfd$var$wildValues.forEach((value)=>{
        for(let i = 0; i < 4; i++)deck.push({
            color: 'wild',
            value: value,
            emoji: $125bb8bfc3b37dfd$var$emojiMap[value]
        });
    });
    return deck;
}
// Shuffle the deck using Fisher-Yates algorithm
function $125bb8bfc3b37dfd$export$2948b794f685d50b(deck) {
    const shuffled = [
        ...deck
    ];
    for(let i = shuffled.length - 1; i > 0; i--){
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [
            shuffled[j],
            shuffled[i]
        ];
    }
    return shuffled;
}
// Deal cards to players
function $125bb8bfc3b37dfd$export$bc03ba615e47b5f3(players, deck, cardsPerPlayer) {
    console.log(`Dealing ${cardsPerPlayer} cards to each player`);
    for(let i = 0; i < cardsPerPlayer; i++)players.forEach((player)=>{
        player.hand.push(deck.pop());
    });
}


// Bluey character names - now includes Dad (replacing Coco)
const $d2949551220fa082$export$682740a95cab13e = [
    'Bluey',
    'Bingo',
    'Bandit',
    'Dad'
];
// Create players (1 human, rest AI)
function $d2949551220fa082$export$94a3c07841793e28(numPlayers) {
    const players = [];
    // Create human player (Julia)
    players.push({
        name: 'Julia',
        hand: [],
        isAI: false,
        hasCalledUno: false
    });
    // Create AI players with specific Bluey characters
    // We'll use Bluey, Bingo, and Dad in that priority order
    const priorityCharacters = [
        'Bluey',
        'Bingo',
        'Dad'
    ];
    // Use as many characters as needed based on numPlayers (1-3 AI players)
    for(let i = 0; i < Math.min(numPlayers - 1, priorityCharacters.length); i++)players.push({
        name: priorityCharacters[i],
        hand: [],
        isAI: true,
        hasCalledUno: false
    });
    return players;
}
// Get random Bluey characters without repeats
function $d2949551220fa082$var$getRandomBlueyCharacters(count) {
    // Shuffle the array
    const shuffled = [
        ...$d2949551220fa082$export$682740a95cab13e
    ];
    for(let i = shuffled.length - 1; i > 0; i--){
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [
            shuffled[j],
            shuffled[i]
        ];
    }
    // Return the first 'count' characters
    return shuffled.slice(0, count);
}




// Character image data and functionality
// Character colors for the colored circle fallbacks
const $6d4b17afc66be304$export$dac48ef76460b48c = {
    'Bluey': '#1E90FF',
    'Bingo': '#FF6B6B',
    'Bandit': '#4169E1',
    'Julia': '#FFCC66',
    'Dad': '#5F4B32' // Brown for Dad
};
// Simple function to get HTML for a character display with initial
function $6d4b17afc66be304$export$4c7b48d039c54e00(characterName) {
    // Create a container div for the character display
    const container = document.createElement('div');
    container.className = 'character-display';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.position = 'relative';
    // Get the background color
    const bgColor = $6d4b17afc66be304$export$dac48ef76460b48c[characterName] || '#4682B4'; // Default if not found
    // Create the circle with initial
    const circleDiv = document.createElement('div');
    circleDiv.className = 'character-initial';
    circleDiv.textContent = characterName.charAt(0);
    circleDiv.style.backgroundColor = bgColor;
    circleDiv.style.width = '100%';
    circleDiv.style.height = '100%';
    circleDiv.style.borderRadius = '50%';
    circleDiv.style.display = 'flex';
    circleDiv.style.justifyContent = 'center';
    circleDiv.style.alignItems = 'center';
    circleDiv.style.color = 'white';
    circleDiv.style.fontSize = '60px';
    circleDiv.style.fontWeight = 'bold';
    circleDiv.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
    container.appendChild(circleDiv);
    // Return the HTML string
    return container.outerHTML;
}


// Helper function to apply common styles to elements
function $84923368ea4a1ab4$var$applyStyles(element, styles) {
    for (const [property, value] of Object.entries(styles))element.style[property] = value;
    return element;
}
// Initialize the game board
function $84923368ea4a1ab4$export$37af8f2807572b25() {
    console.log('Starting renderGame');
    // Render all game elements
    $84923368ea4a1ab4$var$renderOpponents();
    $84923368ea4a1ab4$var$renderDeckAndDiscardPile();
    $84923368ea4a1ab4$var$renderPlayerHand();
    $84923368ea4a1ab4$var$setupColorChoiceUI();
    $84923368ea4a1ab4$var$updateTurnIndicator();
    // Initialize direction indicator with the current game direction
    $84923368ea4a1ab4$var$updateDirectionIndicator((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).direction);
    // Render Julia if the function exists
    if (typeof $84923368ea4a1ab4$var$renderJulia === 'function') $84923368ea4a1ab4$var$renderJulia();
    // Always call renderBingo if the function exists, even for 2-player games
    // The function will handle hiding itself when needed
    if (typeof $84923368ea4a1ab4$export$cd5f809e84a70070 === 'function') $84923368ea4a1ab4$export$cd5f809e84a70070();
    // Always call renderDad, and let it decide whether to show itself
    if (typeof $84923368ea4a1ab4$export$7e528ffc4843bd4f === 'function') $84923368ea4a1ab4$export$7e528ffc4843bd4f();
    console.log('Initial renderGame complete');
}
// Update the turn indicator to show whose turn it is visually
function $84923368ea4a1ab4$var$updateTurnIndicator() {
    const turnIndicator = document.querySelector('.turn-indicator');
    if (!turnIndicator) return; // Safety check
    if (!(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isGameStarted) {
        // Hide the turn indicator before game starts
        turnIndicator.style.display = 'none';
        // Make sure name turn indicator is also hidden
        $84923368ea4a1ab4$var$updateNameTurnIndicator(null);
        return;
    }
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === 0) // Your turn - show the arrow indicator
    turnIndicator.style.display = 'block';
    else // Not your turn - hide the indicator
    turnIndicator.style.display = 'none';
    // Also highlight the active player in the opponents area
    const opponents = document.querySelectorAll('.opponent');
    opponents.forEach((opponent, index)=>{
        if (index + 1 === (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex) opponent.classList.add('current-player');
        else opponent.classList.remove('current-player');
    });
    // Update the name-based turn indicator
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length > 0) {
        const currentPlayer = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players[(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex];
        $84923368ea4a1ab4$var$updateNameTurnIndicator(currentPlayer?.name || null);
    } else $84923368ea4a1ab4$var$updateNameTurnIndicator(null);
}
// Update the direction indicator based on game direction
function $84923368ea4a1ab4$var$updateDirectionIndicator(direction) {
    const directionIndicator = document.getElementById('direction-indicator');
    if (!directionIndicator) return;
    // Set the style for the direction indicator - smaller to match color indicator
    directionIndicator.style.width = '40px'; // Reduced size
    directionIndicator.style.height = '40px'; // Reduced size
    directionIndicator.style.display = 'flex';
    directionIndicator.style.justifyContent = 'center';
    directionIndicator.style.alignItems = 'center';
    directionIndicator.style.fontSize = '30px'; // Smaller icon size
    directionIndicator.style.position = 'relative';
    directionIndicator.style.marginTop = '10px'; // Less margin
    // Set the emoji based on direction
    if (direction === 1) {
        // Clockwise
        directionIndicator.innerHTML = "\uD83D\uDD01";
        directionIndicator.title = 'Clockwise Direction';
        // Add animation for clockwise rotation
        directionIndicator.style.animation = 'rotate-clockwise 3s linear infinite';
        // Add text shadow for visibility without background
        directionIndicator.style.textShadow = '0 0 15px white, 0 0 10px white';
    } else {
        // Counter-clockwise
        directionIndicator.innerHTML = "\uD83D\uDD04";
        directionIndicator.title = 'Counter-Clockwise Direction';
        // Add animation for counter-clockwise rotation
        directionIndicator.style.animation = 'rotate-counter-clockwise 3s linear infinite';
        // Add text shadow for visibility without background
        directionIndicator.style.textShadow = '0 0 15px white, 0 0 10px white';
    }
    // Make sure the animation style is defined
    if (!document.getElementById('direction-animations')) {
        const style = document.createElement('style');
        style.id = 'direction-animations';
        style.textContent = `
      @keyframes rotate-clockwise {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes rotate-counter-clockwise {
        from { transform: rotate(0deg); }
        to { transform: rotate(-360deg); }
      }
    `;
        document.head.appendChild(style);
    }
}
// Create or update the name-based turn indicator next to the color indicator
function $84923368ea4a1ab4$var$updateNameTurnIndicator(playerName) {
    // Find the side indicators container
    const sideIndicators = document.getElementById('side-indicators');
    if (!sideIndicators) return;
    // Look for existing turn indicator or create a new one
    let nameTurnIndicator = document.getElementById('name-turn-indicator');
    if (!nameTurnIndicator) {
        // Create new turn indicator
        nameTurnIndicator = document.createElement('div');
        nameTurnIndicator.id = 'name-turn-indicator';
        // Style the indicator - no background
        nameTurnIndicator.style.padding = '10px 15px';
        nameTurnIndicator.style.fontWeight = 'bold';
        nameTurnIndicator.style.fontSize = '18px';
        nameTurnIndicator.style.marginTop = '15px'; // Reduced margin
        nameTurnIndicator.style.fontFamily = "'Comic Sans MS', 'Comic Sans', cursive, sans-serif";
        nameTurnIndicator.style.textAlign = 'center';
        nameTurnIndicator.style.minWidth = '100px'; // Smaller width
        nameTurnIndicator.style.position = 'relative';
        // Add a label above the indicator
        const turnLabel = document.createElement('div');
        turnLabel.textContent = "Current Turn";
        turnLabel.style.fontSize = '14px';
        turnLabel.style.fontWeight = 'bold';
        turnLabel.style.color = 'white';
        turnLabel.style.textShadow = '0 1px 3px rgba(0, 0, 0, 0.5)';
        turnLabel.style.position = 'absolute';
        turnLabel.style.top = '-25px';
        turnLabel.style.left = '50%';
        turnLabel.style.transform = 'translateX(-50%)';
        turnLabel.style.width = '100%';
        turnLabel.style.textAlign = 'center';
        nameTurnIndicator.appendChild(turnLabel);
        // Add after the color indicator
        sideIndicators.appendChild(nameTurnIndicator);
    }
    // Set or clear content based on playerName
    if (playerName) {
        nameTurnIndicator.style.display = 'block';
        // Use inner span for the actual player name for styling purposes
        nameTurnIndicator.innerHTML = `<div style="position: absolute; top: -25px; left: 50%; transform: translateX(-50%); font-weight: bold; color: white; text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5); font-size: 14px; width: 100%; text-align: center;">Current Turn</div><span>${playerName}</span>`;
        // Set color based on player name
        if (playerName === 'Bluey') {
            nameTurnIndicator.style.color = 'white';
            nameTurnIndicator.style.textShadow = '0 0 10px #1E90FF, 0 0 15px #1E90FF';
        } else if (playerName === 'Bingo') {
            nameTurnIndicator.style.color = 'white';
            nameTurnIndicator.style.textShadow = '0 0 10px #FF6B6B, 0 0 15px #FF6B6B';
        } else if (playerName === 'Dad') {
            nameTurnIndicator.style.color = 'white';
            nameTurnIndicator.style.textShadow = '0 0 10px #5F4B32, 0 0 15px #5F4B32';
        } else if (playerName === 'Bandit') {
            nameTurnIndicator.style.color = 'white';
            nameTurnIndicator.style.textShadow = '0 0 10px #4169E1, 0 0 15px #4169E1';
        } else if (playerName === 'Julia') {
            nameTurnIndicator.style.color = '#333';
            nameTurnIndicator.style.textShadow = '0 0 10px #FFCC66, 0 0 15px #FFCC66';
        } else {
            // Default fallback
            nameTurnIndicator.style.color = '#333';
            nameTurnIndicator.style.textShadow = '0 0 10px white, 0 0 15px white';
        }
    } else // No current player, hide the indicator
    nameTurnIndicator.style.display = 'none';
}
// Create character name element
function $84923368ea4a1ab4$var$createCharacterName(characterName, isNameOnly = false) {
    return $84923368ea4a1ab4$var$createNameBadge(characterName, false);
}
// Create card badge for displaying card count
function $84923368ea4a1ab4$var$createCardBadge(cards) {
    const cardBadge = document.createElement('div');
    cardBadge.className = 'card-badge';
    cardBadge.innerHTML = `\u{1F0CF} <span>${cards}</span>`;
    cardBadge.style.position = 'absolute';
    cardBadge.style.bottom = '-15px';
    cardBadge.style.right = '-15px';
    cardBadge.style.backgroundColor = 'white';
    cardBadge.style.color = '#333';
    cardBadge.style.padding = '5px 12px';
    cardBadge.style.borderRadius = '20px';
    cardBadge.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
    cardBadge.style.fontSize = '20px';
    cardBadge.style.fontWeight = 'bold';
    return cardBadge;
}
// Create a container for opponent cards
function $84923368ea4a1ab4$var$createCardsContainer(player) {
    const cardsContainer = document.createElement('div');
    cardsContainer.className = 'opponent-cards';
    cardsContainer.style.position = 'relative';
    cardsContainer.style.display = 'flex';
    cardsContainer.style.flexDirection = 'row';
    cardsContainer.style.justifyContent = 'center';
    cardsContainer.style.marginTop = '20px';
    cardsContainer.style.minHeight = '70px';
    cardsContainer.style.width = '110%';
    cardsContainer.style.maxWidth = '350px';
    cardsContainer.style.flexWrap = 'wrap';
    // Create a visual representation of cards
    const numCards = player.hand.length;
    // Limit the number of displayed cards
    const maxVisibleCards = 7;
    const cardsToShow = Math.min(numCards, maxVisibleCards);
    // Create and add cards
    for(let i = 0; i < cardsToShow; i++){
        const card = document.createElement('div');
        card.className = 'opponent-card';
        // Use different styling when there's only one card left
        if (numCards === 1) {
            // Style for UNO card
            card.style.width = '40px';
            card.style.height = '56px';
            card.style.backgroundColor = '#ff3b3b';
            card.style.margin = '0 4px';
            card.style.borderRadius = '8px';
            card.style.border = '2px solid white';
            card.style.boxShadow = '0 2px 8px rgba(255, 0, 0, 0.5)';
            if (player.hasCalledUno) {
                // Add UNO text on the card
                const unoText = document.createElement('div');
                unoText.textContent = 'UNO';
                unoText.style.color = 'white';
                unoText.style.fontWeight = 'bold';
                unoText.style.fontSize = '12px';
                unoText.style.transform = 'rotate(-45deg)';
                unoText.style.position = 'absolute';
                unoText.style.top = '50%';
                unoText.style.left = '50%';
                unoText.style.transform = 'translate(-50%, -50%) rotate(-45deg)';
                card.appendChild(unoText);
            }
        } else {
            // Standard card styling
            card.style.width = '28px';
            card.style.height = '38px';
            card.style.backgroundColor = '#ff5757';
            card.style.margin = '0 -5px'; // Overlap cards
            card.style.borderRadius = '5px';
            card.style.border = '2px solid white';
            card.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
        }
        cardsContainer.appendChild(card);
    }
    // If there are more cards than we're showing, add indicator
    if (numCards > maxVisibleCards) {
        const moreCards = document.createElement('div');
        moreCards.className = 'more-cards';
        moreCards.textContent = `+${numCards - maxVisibleCards}`;
        moreCards.style.marginLeft = '5px';
        moreCards.style.fontWeight = 'bold';
        moreCards.style.opacity = '0.7';
        cardsContainer.appendChild(moreCards);
    }
    return cardsContainer;
}
// Create name badge for characters
function $84923368ea4a1ab4$var$createNameBadge(name, isLarge = false) {
    const badge = document.createElement('div');
    badge.className = 'name-badge';
    badge.textContent = name;
    // Base styling
    badge.style.backgroundColor = 'white';
    badge.style.color = '#333';
    badge.style.borderRadius = '15px';
    badge.style.padding = isLarge ? '8px 15px' : '4px 10px';
    badge.style.fontWeight = 'bold';
    badge.style.fontSize = isLarge ? '22px' : '16px';
    badge.style.marginTop = '8px';
    badge.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
    badge.style.fontFamily = "'Comic Sans MS', 'Comic Sans', cursive, sans-serif";
    return badge;
}
// Render the opponents (Bluey characters)
function $84923368ea4a1ab4$var$renderOpponents() {
    const opponentsArea = document.getElementById('opponents-area');
    opponentsArea.innerHTML = '';
    // Reset Dad display flag since we're rebuilding the opponents
    document.body.dataset.dadDisplayed = "false";
    // Set up the layout
    opponentsArea.style.display = 'flex';
    opponentsArea.style.justifyContent = 'space-evenly'; // Use space-evenly for even distribution
    opponentsArea.style.width = '100%';
    opponentsArea.style.maxWidth = '900px';
    opponentsArea.style.margin = '0 auto';
    opponentsArea.style.position = 'relative'; // Ensure relative positioning for absolute children
    // Get the total number of expected opponents (excluding player 0 which is human)
    const totalOpponents = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players ? (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length - 1 : 3;
    const isFourPlayerGame = totalOpponents >= 3;
    // Define positions based on how many opponents we have
    // Instead of using fixed position, we'll now apply styles differently
    // based on the container justification which is space-evenly
    let positions;
    if (totalOpponents === 1) // For 2-player games (1 opponent), center the opponent
    // We'll handle this with a different approach
    positions = [
        {
            id: 'opponent-bluey'
        },
        {
            id: 'opponent-bingo'
        },
        {
            id: 'opponent-bandit'
        } // Unused slot
    ];
    else if (isFourPlayerGame) // For 4-player games with 3 opponents, rely on space-evenly
    positions = [
        {
            id: 'opponent-bluey'
        },
        {
            id: 'opponent-bingo'
        },
        {
            id: 'opponent-bandit'
        } // Bandit/Dad
    ];
    else // For 3-player games with 2 opponents
    positions = [
        {
            id: 'opponent-bluey'
        },
        {
            id: 'opponent-bingo'
        },
        {
            id: 'opponent-bandit'
        } // Bandit
    ];
    // Create placeholders for all 3 potential opponent slots
    for(let i = 0; i < 3; i++){
        const opponentSlot = document.createElement('div');
        opponentSlot.className = 'opponent-slot';
        opponentSlot.style.minWidth = '200px';
        opponentSlot.style.minHeight = '200px';
        opponentSlot.style.display = 'flex';
        opponentSlot.style.flexDirection = 'column';
        opponentSlot.style.alignItems = 'center';
        opponentSlot.style.justifyContent = 'center';
        // Apply styling to the slots
        opponentSlot.style.position = 'relative';
        // Set ID for the opponent slot for better identification
        if (positions[i] && positions[i].id) opponentSlot.id = positions[i].id;
        // Special case for single opponent (center it)
        if (totalOpponents === 1 && i === 0) opponentSlot.style.margin = '0 auto'; // Center the slot
        // We only display Bluey in the main renderOpponents function
        // Bingo and Dad/Bandit are handled in their own functions
        if (i === 0) {
            const playerIndex = 1; // Bluey is index 1 (human is index 0)
            if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && playerIndex < (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length) {
                // This is the Bluey player
                const player = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players[playerIndex];
                const opponent = document.createElement('div');
                opponent.className = 'opponent';
                // Add highlight for current player
                if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === playerIndex) opponent.classList.add('current-player');
                // Create UNO indicator if needed
                if (player.hand.length === 1 && player.hasCalledUno) {
                    const unoIndicator = document.createElement('div');
                    unoIndicator.className = 'uno-indicator';
                    unoIndicator.textContent = 'UNO!';
                    opponent.appendChild(unoIndicator);
                }
                // Create and add the image for Bluey
                const characterImage = document.createElement('div');
                characterImage.className = 'character-image';
                characterImage.style.width = '80px'; // Reduce size to 80px
                characterImage.style.height = '80px'; // Reduce size to 80px
                characterImage.style.position = 'relative';
                // For Bluey, use the createCharacterDisplay function
                const blueyDisplay = $84923368ea4a1ab4$var$createCharacterDisplay('Bluey', 80); // Reduce size to 80px
                characterImage.appendChild(blueyDisplay);
                opponent.appendChild(characterImage);
                // Add cards container to show visual representation of cards
                opponent.appendChild($84923368ea4a1ab4$var$createCardsContainer(player));
                // Add the opponent to the slot
                opponentSlot.appendChild(opponent);
            }
        }
        // Add the slot to the area regardless of whether it contains a player
        opponentsArea.appendChild(opponentSlot);
    }
    // Call renderBingo and renderDad to fill in their slots
    $84923368ea4a1ab4$export$cd5f809e84a70070();
    $84923368ea4a1ab4$export$7e528ffc4843bd4f();
}
// Render the deck and discard pile
function $84923368ea4a1ab4$var$renderDeckAndDiscardPile() {
    const deckElement = document.getElementById('deck');
    const discardPileElement = document.getElementById('discard-pile');
    // Render deck
    deckElement.innerHTML = '';
    // Create better deck back design
    const deckBack = document.createElement('div');
    deckBack.className = 'card-back';
    deckBack.style.width = '96px'; /* Reduced by 20% from 120px */ 
    // Draw indicators removed per issue #38
    deckBack.style.height = '134px'; /* Reduced by 20% from 168px */ 
    deckBack.style.borderRadius = '10px';
    deckBack.style.backgroundColor = '#ff5757';
    deckBack.style.border = '3px solid white';
    deckBack.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
    deckBack.style.position = 'relative';
    deckBack.style.display = 'flex';
    deckBack.style.justifyContent = 'center';
    deckBack.style.alignItems = 'center';
    // Add UNO logo to deck back
    const unoLogo = document.createElement('div');
    unoLogo.textContent = 'UNO';
    unoLogo.style.color = 'white';
    unoLogo.style.fontSize = '32px';
    unoLogo.style.fontWeight = 'bold';
    unoLogo.style.transform = 'rotate(-45deg)';
    unoLogo.style.textShadow = '0 2px 5px rgba(0, 0, 0, 0.5)';
    unoLogo.style.letterSpacing = '2px';
    deckBack.appendChild(unoLogo);
    // Add card count to deck
    const deckCount = document.createElement('div');
    deckCount.className = 'deck-count';
    deckCount.textContent = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).deck.length.toString();
    deckCount.style.position = 'absolute';
    deckCount.style.top = '-10px';
    deckCount.style.right = '-10px';
    deckCount.style.backgroundColor = 'white';
    deckCount.style.color = '#333';
    deckCount.style.borderRadius = '50%';
    deckCount.style.width = '30px';
    deckCount.style.height = '30px';
    deckCount.style.display = 'flex';
    deckCount.style.justifyContent = 'center';
    deckCount.style.alignItems = 'center';
    deckCount.style.fontWeight = 'bold';
    deckCount.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
    deckCount.style.border = '2px solid #ff5757';
    deckBack.appendChild(deckCount);
    deckElement.appendChild(deckBack);
    // Add click handler for drawing cards
    deckElement.onclick = ()=>{
        // Allow drawing when it's either:
        // 1. The player's turn, OR
        // 2. The player has required draws to make (from Draw 2 or Draw 4)
        if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === 0 || (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isDrawingCards) {
            if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).requiredDraws > 0) {
                // If player needs to draw cards due to Draw 2 or Draw 4
                (0, $4ee2b06a9c1953f0$export$ebab430dd53d463d)();
                // Remove 'required-draw' and 'active-deck' classes when no more draws required
                if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).requiredDraws === 0) {
                    deckElement.classList.remove('required-draw');
                    deckElement.classList.remove('active-deck');
                }
            } else if (!(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).waitingForColorChoice) // Regular draw
            (0, $4ee2b06a9c1953f0$export$ebab430dd53d463d)((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172));
        }
    };
    // Add required draw indicators and styling if needed
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).requiredDraws > 0) {
        // Remove any existing indicators first
        const existingIndicators = document.querySelectorAll('.draw-indicator, .deck-reminder');
        existingIndicators.forEach((reminder)=>{
            if (reminder.parentElement) reminder.parentElement.removeChild(reminder);
        });
        if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).requiredDraws > 0) {
            deckElement.classList.add('required-draw');
            deckElement.classList.add('active-deck');
        // Visual indicators removed per issue #38
        }
    }
    // Render discard pile
    discardPileElement.innerHTML = '';
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.length > 0) {
        // Get up to 10 most recent cards from discard pile
        const visibleCardCount = Math.min((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.length, 10);
        const visibleCards = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.slice(-visibleCardCount);
        // Create a container for the offset cards
        const cardsContainer = document.createElement('div');
        cardsContainer.className = 'discard-cards-container';
        cardsContainer.style.position = 'relative';
        cardsContainer.style.width = '100%';
        cardsContainer.style.height = '100%';
        cardsContainer.style.display = 'flex';
        cardsContainer.style.alignItems = 'center';
        cardsContainer.style.justifyContent = 'flex-start';
        cardsContainer.style.overflow = 'visible';
        // Add cards with offset
        visibleCards.forEach((card, index)=>{
            const isTopCard = index === visibleCards.length - 1;
            // Use true for all cards to make them all the same size
            const cardElement = $84923368ea4a1ab4$var$createCardElement(card, true);
            // Add data attribute for card value (for CSS targeting)
            cardElement.setAttribute('data-value', card.value);
            // Add color class to the card
            cardElement.classList.add(card.color);
            // Calculate offset - move each card 40px to the right of the previous one for better visibility
            const offsetX = index * 40;
            cardElement.style.position = 'absolute';
            cardElement.style.left = `${offsetX}px`;
            // No vertical offset - keep cards on the same level
            cardElement.style.top = '0';
            cardElement.style.setProperty('--index', index); // Set CSS variable for responsive adjustments
            cardElement.style.zIndex = index + 1; // Ensure proper stacking order
            // Add glow only to the top card
            if (isTopCard) cardElement.style.boxShadow = `0 0 15px rgba(255, 255, 255, 0.7), 0 4px 8px rgba(0, 0, 0, 0.3)`;
            cardsContainer.appendChild(cardElement);
        });
        discardPileElement.appendChild(cardsContainer);
        // Show the current color based on the top card
        const topCard = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile[(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.length - 1];
        if ((topCard.value === 'Wild' || topCard.value === 'Wild Draw 4') && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentColor) $84923368ea4a1ab4$var$updateColorIndicator((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentColor);
        else $84923368ea4a1ab4$var$updateColorIndicator(topCard.color);
    } else {
        // Show empty placeholder for discard pile
        const emptyPile = document.createElement('div');
        emptyPile.className = 'empty-discard';
        emptyPile.style.width = '96px'; /* Reduced by 20% from 120px */ 
        emptyPile.style.height = '134px'; /* Reduced by 20% from 168px */ 
        emptyPile.style.borderRadius = '10px';
        emptyPile.style.border = '3px dashed rgba(255, 255, 255, 0.5)';
        emptyPile.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        discardPileElement.appendChild(emptyPile);
        // Hide color indicator when no cards are in play
        $84923368ea4a1ab4$var$updateColorIndicator(null);
    }
}
// Update the color indicator on the side of the screen
function $84923368ea4a1ab4$var$updateColorIndicator(color) {
    const indicator = document.getElementById('color-indicator');
    if (!indicator) return;
    if (!color) {
        // Hide indicator if no color
        indicator.style.display = 'none';
        return;
    }
    // Show and update indicator
    indicator.style.display = 'block';
    // Map color names to actual CSS colors
    const colorMap = {
        'red': '#ff3b3b',
        'blue': '#4a7af5',
        'green': '#47c83e',
        'yellow': '#ffee3e'
    };
    const cssColor = colorMap[color.toLowerCase()] || '#ffffff';
    // Style the indicator - make it half the size
    indicator.style.backgroundColor = cssColor;
    indicator.style.width = '15px'; // Half the previous width
    indicator.style.height = '75px'; // Half the previous height
    indicator.style.borderRadius = '8px'; // Adjusted for smaller size
    indicator.style.boxShadow = `0 0 15px ${cssColor}88`; // Slightly smaller shadow
    indicator.style.border = '2px solid white'; // Thinner border for smaller indicator
    // Make sure we also update the direction indicator with the current game direction
    $84923368ea4a1ab4$var$updateDirectionIndicator((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).direction);
}
// Render the player's hand
function $84923368ea4a1ab4$var$renderPlayerHand() {
    const playerHandElement = document.getElementById('player-hand');
    playerHandElement.innerHTML = '';
    // Removed the "Your Cards" title as it's obvious
    // Get player's hand
    const playerHand = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players[0].hand;
    // Create container for cards
    const cardsContainer = document.createElement('div');
    cardsContainer.className = 'player-cards';
    cardsContainer.style.display = 'flex';
    cardsContainer.style.justifyContent = 'center';
    cardsContainer.style.flexWrap = 'wrap';
    cardsContainer.style.gap = '10px';
    playerHandElement.appendChild(cardsContainer);
    // Generate card elements
    playerHand.forEach((card, index)=>{
        const cardElement = $84923368ea4a1ab4$var$createCardElement(card);
        // Add data attribute for card value (for CSS targeting)
        cardElement.setAttribute('data-value', card.value);
        // Add color class to the card
        cardElement.classList.add(card.color);
        // Add active class if it's the player's turn and the card is playable
        if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === 0 && !(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).waitingForColorChoice && $84923368ea4a1ab4$var$isCardPlayable(card)) {
            cardElement.classList.add('playable-card');
            // Add click handler for playable cards
            cardElement.onclick = ()=>{
                // Play the card
                (0, $4ee2b06a9c1953f0$export$317a6377ae525e39)((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172), 0, index);
            };
        }
        cardsContainer.appendChild(cardElement);
    });
    // Automatically call UNO if player has 2 cards left and plays one
    if (playerHand.length === 2 && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === 0 && !(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players[0].hasCalledUno) // Set UNO flag automatically - no button needed
    (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players[0].hasCalledUno = true;
}
// Setup color choice UI
function $84923368ea4a1ab4$var$setupColorChoiceUI() {
    const colorChoiceElement = document.getElementById('color-choice');
    const colorButtonsElement = document.getElementById('color-buttons');
    if (!colorChoiceElement || !colorButtonsElement) return;
    // Clear existing buttons
    colorButtonsElement.innerHTML = '';
    // Hide or show color choice based on game state
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).waitingForColorChoice) {
        colorChoiceElement.style.display = 'flex';
        colorChoiceElement.style.flexDirection = 'column';
        colorChoiceElement.style.alignItems = 'center';
        colorChoiceElement.style.justifyContent = 'center';
        colorChoiceElement.style.position = 'fixed';
        colorChoiceElement.style.top = '50%';
        colorChoiceElement.style.left = '50%';
        colorChoiceElement.style.transform = 'translate(-50%, -50%)';
        colorChoiceElement.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        colorChoiceElement.style.padding = '30px';
        colorChoiceElement.style.borderRadius = '20px';
        colorChoiceElement.style.boxShadow = '0 0 0 2000px rgba(0, 0, 0, 0.5), 0 5px 25px rgba(0, 0, 0, 0.5)';
        colorChoiceElement.style.zIndex = '1000';
        // Generate color buttons
        const colorOptions = [
            'red',
            'blue',
            'green',
            'yellow'
        ];
        // Create container for buttons
        colorButtonsElement.style.display = 'flex';
        colorButtonsElement.style.justifyContent = 'center';
        colorButtonsElement.style.gap = '15px';
        colorButtonsElement.style.marginTop = '15px';
        // Map of colors to emoji
        const colorEmojis = {
            'red': "\u2764\uFE0F",
            'blue': "\uD83D\uDC99",
            'green': "\uD83D\uDC9A",
            'yellow': "\uD83D\uDC9B"
        };
        // Create buttons for each color
        colorOptions.forEach((color)=>{
            const button = document.createElement('button');
            button.className = 'color-button';
            button.dataset.color = color;
            // Style the button
            button.style.width = '80px';
            button.style.height = '80px';
            button.style.borderRadius = '50%';
            button.style.border = 'none';
            button.style.cursor = 'pointer';
            button.style.fontSize = '40px';
            button.style.display = 'flex';
            button.style.justifyContent = 'center';
            button.style.alignItems = 'center';
            button.style.transition = 'transform 0.2s';
            // Set color-specific styles
            const colorMap = {
                'red': '#ff3b3b',
                'blue': '#4a7af5',
                'green': '#47c83e',
                'yellow': '#ffee3e'
            };
            button.style.backgroundColor = colorMap[color];
            button.style.boxShadow = `0 4px 10px ${colorMap[color]}88`;
            button.innerHTML = colorEmojis[color];
            // Add hover effect
            button.onmouseenter = ()=>{
                button.style.transform = 'scale(1.1)';
            };
            button.onmouseleave = ()=>{
                button.style.transform = 'scale(1)';
            };
            // Add click handler
            button.onclick = ()=>{
                (0, $4ee2b06a9c1953f0$export$3d51a2be9557512a)((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172), color);
                // Force hide the color choice UI
                colorChoiceElement.style.display = 'none';
                // Make sure the waitingForColorChoice flag is set to false
                (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).waitingForColorChoice = false;
            };
            colorButtonsElement.appendChild(button);
        });
    } else colorChoiceElement.style.display = 'none';
}
// Helper to determine if a card is playable based on the current game state
function $84923368ea4a1ab4$var$isCardPlayable(card) {
    // If required draws are pending, no cards are playable
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).requiredDraws > 0) return false;
    // If waiting for color choice, no cards are playable
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).waitingForColorChoice) return false;
    // Check if discard pile is empty
    if (!(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile || (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.length === 0) // If no cards in discard pile, any card is playable
    return true;
    const topCard = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile[(0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).discardPile.length - 1];
    // Wild cards are always playable
    if (card.value === 'Wild' || card.value === 'Wild Draw 4') return true;
    // If top card is wild, match against current color
    if (topCard && (topCard.value === 'Wild' || topCard.value === 'Wild Draw 4') && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentColor) return card.color === (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentColor;
    // Otherwise, match color or value
    return !topCard || card.color === topCard.color || card.value === topCard.value;
}
// Create a card element for display
function $84923368ea4a1ab4$var$createCardElement(card, isDiscardPile = false) {
    const cardElement = document.createElement('div');
    cardElement.className = 'card';
    // Base card styling - reduced size by 20% for all cards
    cardElement.style.width = isDiscardPile ? '112px' : '64px'; /* Reduced by 20% */ 
    cardElement.style.height = isDiscardPile ? '157px' : '90px'; /* Reduced by 20% */ 
    cardElement.style.borderRadius = '10px';
    cardElement.style.border = '3px solid white';
    cardElement.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
    cardElement.style.position = 'relative';
    cardElement.style.display = 'flex';
    cardElement.style.flexDirection = 'column';
    cardElement.style.justifyContent = 'center';
    cardElement.style.alignItems = 'center';
    cardElement.style.transition = 'transform 0.2s';
    cardElement.style.overflow = 'hidden';
    // Color-specific styling
    const colorMap = {
        'red': '#ff3b3b',
        'blue': '#4a7af5',
        'green': '#47c83e',
        'yellow': '#ffee3e'
    };
    // Create card background with color
    if (card.value === 'Wild' || card.value === 'Wild Draw 4') {
        // Special styling for wild cards - black background with colored sections
        cardElement.style.backgroundColor = '#333';
        // Create a colorful pattern for wild cards
        const wildPattern = document.createElement('div');
        wildPattern.style.position = 'absolute';
        wildPattern.style.top = '0';
        wildPattern.style.left = '0';
        wildPattern.style.right = '0';
        wildPattern.style.bottom = '0';
        wildPattern.style.zIndex = '0';
        // Create quadrants for the wild card
        const colors = [
            '#ff3b3b',
            '#4a7af5',
            '#47c83e',
            '#ffee3e'
        ];
        const tl = document.createElement('div');
        tl.style.position = 'absolute';
        tl.style.top = '0';
        tl.style.left = '0';
        tl.style.width = '50%';
        tl.style.height = '50%';
        tl.style.backgroundColor = colors[0];
        const tr = document.createElement('div');
        tr.style.position = 'absolute';
        tr.style.top = '0';
        tr.style.right = '0';
        tr.style.width = '50%';
        tr.style.height = '50%';
        tr.style.backgroundColor = colors[1];
        const bl = document.createElement('div');
        bl.style.position = 'absolute';
        bl.style.bottom = '0';
        bl.style.left = '0';
        bl.style.width = '50%';
        bl.style.height = '50%';
        bl.style.backgroundColor = colors[2];
        const br = document.createElement('div');
        br.style.position = 'absolute';
        br.style.bottom = '0';
        br.style.right = '0';
        br.style.width = '50%';
        br.style.height = '50%';
        br.style.backgroundColor = colors[3];
        wildPattern.appendChild(tl);
        wildPattern.appendChild(tr);
        wildPattern.appendChild(bl);
        wildPattern.appendChild(br);
        cardElement.appendChild(wildPattern);
    } else cardElement.style.backgroundColor = colorMap[card.color.toLowerCase()];
    // Create center emoji container (without white oval background)
    const centerCircle = document.createElement('div');
    centerCircle.style.position = 'absolute';
    centerCircle.style.width = isDiscardPile ? '100px' : '55px';
    centerCircle.style.height = isDiscardPile ? '100px' : '55px';
    centerCircle.style.display = 'flex';
    centerCircle.style.justifyContent = 'center';
    centerCircle.style.alignItems = 'center';
    centerCircle.style.zIndex = '1';
    // Add emoji for card value with larger size
    const emoji = document.createElement('div');
    emoji.textContent = card.emoji;
    emoji.style.fontSize = isDiscardPile ? '80px' : '40px';
    emoji.style.lineHeight = '1';
    emoji.style.textShadow = '0 0 3px rgba(0, 0, 0, 0.3)';
    centerCircle.appendChild(emoji);
    cardElement.appendChild(centerCircle);
    // Add card value to top-left and bottom-right corners with better styling
    const topValue = document.createElement('div');
    topValue.className = 'card-corner top-left';
    // For special cards, use shortened display text
    let displayValue = card.value;
    if (card.value === 'Skip') displayValue = 'S';
    if (card.value === 'Reverse') displayValue = 'R';
    if (card.value === 'Draw 2') displayValue = '+2';
    if (card.value === 'Wild') displayValue = 'W';
    if (card.value === 'Wild Draw 4') displayValue = 'W4';
    topValue.textContent = displayValue;
    topValue.style.position = 'absolute';
    topValue.style.top = isDiscardPile ? '5px' : '3px';
    topValue.style.left = isDiscardPile ? '5px' : '3px';
    topValue.style.fontSize = isDiscardPile ? '18px' : '12px';
    topValue.style.fontWeight = 'bold';
    topValue.style.color = 'white';
    topValue.style.zIndex = '2';
    topValue.style.padding = '2px';
    topValue.style.display = 'flex';
    topValue.style.justifyContent = 'center';
    topValue.style.alignItems = 'center';
    const bottomValue = document.createElement('div');
    bottomValue.className = 'card-corner bottom-right';
    bottomValue.textContent = displayValue;
    bottomValue.style.position = 'absolute';
    bottomValue.style.bottom = isDiscardPile ? '5px' : '3px';
    bottomValue.style.right = isDiscardPile ? '5px' : '3px';
    bottomValue.style.fontSize = isDiscardPile ? '18px' : '12px';
    bottomValue.style.fontWeight = 'bold';
    bottomValue.style.color = 'white';
    bottomValue.style.transform = 'rotate(180deg)';
    bottomValue.style.zIndex = '2';
    bottomValue.style.padding = '2px';
    bottomValue.style.display = 'flex';
    bottomValue.style.justifyContent = 'center';
    bottomValue.style.alignItems = 'center';
    // Handle text color for yellow cards
    if (card.color === 'yellow') {
        topValue.style.color = 'black';
        bottomValue.style.color = 'black';
    }
    cardElement.appendChild(topValue);
    cardElement.appendChild(bottomValue);
    // Add small diagonal stripes pattern in the background for visual texture
    if (card.color !== 'wild') {
        const stripesPattern = document.createElement('div');
        stripesPattern.style.position = 'absolute';
        stripesPattern.style.top = '0';
        stripesPattern.style.left = '0';
        stripesPattern.style.right = '0';
        stripesPattern.style.bottom = '0';
        stripesPattern.style.opacity = '0.2';
        stripesPattern.style.backgroundImage = 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 255, 255, 0.5) 10px, rgba(255, 255, 255, 0.5) 20px)';
        stripesPattern.style.zIndex = '0';
        cardElement.appendChild(stripesPattern);
    }
    // Add hover effect for playable cards (handled in renderPlayerHand)
    cardElement.onmouseenter = ()=>{
        if (cardElement.classList.contains('playable-card')) {
            cardElement.style.transform = 'translateY(-10px)';
            cardElement.style.boxShadow = '0 12px 20px rgba(0, 0, 0, 0.2)';
        }
    };
    cardElement.onmouseleave = ()=>{
        cardElement.style.transform = 'translateY(0)';
        cardElement.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
    };
    // Add diagonal stripe overlay for Skip cards
    if (card.value === 'Skip') {
        const skipLine = document.createElement('div');
        skipLine.style.position = 'absolute';
        skipLine.style.width = '150%';
        skipLine.style.height = '10px';
        skipLine.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
        skipLine.style.transform = 'rotate(-45deg)';
        skipLine.style.top = '50%';
        skipLine.style.left = '-25%';
        skipLine.style.zIndex = '1';
        skipLine.style.borderTop = '2px solid black';
        skipLine.style.borderBottom = '2px solid black';
        cardElement.appendChild(skipLine);
    }
    // Add arrows for Reverse cards
    if (card.value === 'Reverse') {
        // First arrow pointing right
        const arrowUp = document.createElement('div');
        arrowUp.style.position = 'absolute';
        arrowUp.style.top = '30%';
        arrowUp.style.width = '60%';
        arrowUp.style.height = '10px';
        arrowUp.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
        arrowUp.style.zIndex = '1';
        arrowUp.style.border = '1px solid black';
        cardElement.appendChild(arrowUp);
        // Arrowhead up
        const arrowheadUp = document.createElement('div');
        arrowheadUp.style.position = 'absolute';
        arrowheadUp.style.right = isDiscardPile ? '30px' : '15px';
        arrowheadUp.style.top = '25%';
        arrowheadUp.style.width = '0';
        arrowheadUp.style.height = '0';
        arrowheadUp.style.borderTop = '10px solid transparent';
        arrowheadUp.style.borderBottom = '10px solid transparent';
        arrowheadUp.style.borderLeft = isDiscardPile ? '20px solid rgba(255, 255, 255, 0.8)' : '12px solid rgba(255, 255, 255, 0.8)';
        arrowheadUp.style.zIndex = '1';
        cardElement.appendChild(arrowheadUp);
        // Second arrow pointing left
        const arrowDown = document.createElement('div');
        arrowDown.style.position = 'absolute';
        arrowDown.style.bottom = '30%';
        arrowDown.style.width = '60%';
        arrowDown.style.height = '10px';
        arrowDown.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
        arrowDown.style.zIndex = '1';
        arrowDown.style.border = '1px solid black';
        cardElement.appendChild(arrowDown);
        // Arrowhead down
        const arrowheadDown = document.createElement('div');
        arrowheadDown.style.position = 'absolute';
        arrowheadDown.style.left = isDiscardPile ? '30px' : '15px';
        arrowheadDown.style.bottom = '25%';
        arrowheadDown.style.width = '0';
        arrowheadDown.style.height = '0';
        arrowheadDown.style.borderTop = '10px solid transparent';
        arrowheadDown.style.borderBottom = '10px solid transparent';
        arrowheadDown.style.borderRight = isDiscardPile ? '20px solid rgba(255, 255, 255, 0.8)' : '12px solid rgba(255, 255, 255, 0.8)';
        arrowheadDown.style.zIndex = '1';
        cardElement.appendChild(arrowheadDown);
    }
    // +2 and +4 indicators removed per issue #38
    return cardElement;
}
// Function to update the game display based on the current state
function $84923368ea4a1ab4$export$3f43ff7bf5d42a53(gameState) {
    // Update all UI components
    $84923368ea4a1ab4$var$renderOpponents();
    $84923368ea4a1ab4$var$renderDeckAndDiscardPile();
    $84923368ea4a1ab4$var$renderPlayerHand();
    $84923368ea4a1ab4$var$setupColorChoiceUI();
    $84923368ea4a1ab4$var$updateTurnIndicator();
    $84923368ea4a1ab4$var$updateDirectionIndicator(gameState.direction);
    // Render Julia if function exists
    if (typeof $84923368ea4a1ab4$var$renderJulia === 'function') $84923368ea4a1ab4$var$renderJulia();
    // Always call renderBingo and let it handle visibility based on player count
    if (typeof $84923368ea4a1ab4$export$cd5f809e84a70070 === 'function') $84923368ea4a1ab4$export$cd5f809e84a70070();
    // Always call renderDad and let it handle visibility based on player count
    if (typeof $84923368ea4a1ab4$export$7e528ffc4843bd4f === 'function') $84923368ea4a1ab4$export$7e528ffc4843bd4f();
}
// Create a standard character display with size customization
function $84923368ea4a1ab4$var$createCharacterDisplay(characterName, size = 100) {
    const container = document.createElement('div');
    container.style.width = `${size}px`;
    container.style.height = `${size}px`;
    container.style.position = 'relative';
    // Choose the proper class for fallback
    let colorClass;
    if (characterName === 'Bluey') colorClass = 'bluey-color';
    else if (characterName === 'Bingo') colorClass = 'bingo-color';
    else if (characterName === 'Bandit') colorClass = 'bandit-color';
    else if (characterName === 'Julia') colorClass = 'julia-color';
    else if (characterName === 'Coco') colorClass = 'coco-color';
    // Create fallback first
    const fallback = document.createElement('div');
    fallback.className = `character-fallback ${colorClass}`;
    fallback.textContent = characterName.charAt(0);
    container.appendChild(fallback);
    // Add image element for real image
    const image = document.createElement('img');
    image.className = 'character-img';
    // Get image path from preloaded images in the HTML
    const preloadedImg = document.getElementById(`${characterName.toLowerCase()}-img`);
    if (preloadedImg) {
        // Try to use the preloaded image's source
        image.src = preloadedImg.src;
        // Ensure the image displays when loaded
        image.onload = function() {
            // Once this instance loads, make sure it's visible
            image.style.display = 'block';
        };
        // Handle any loading error in this instance
        image.onerror = function() {
            console.warn(`Failed to load ${characterName} image in UI`);
            // Force direct load on error as a backup
            image.src = `public/images/${characterName.toLowerCase()}.png?direct=${Date.now()}`;
        };
    } else // Fallback path for direct loading with cache-busting
    image.src = `public/images/${characterName.toLowerCase()}.png?ts=${Date.now()}`;
    image.alt = characterName;
    container.appendChild(image);
    // No decorations - removed emoji overlays
    return container;
}
// Render Julia on the left side of the screen
function $84923368ea4a1ab4$var$renderJulia() {
    const juliaDisplay = document.getElementById('julia-display');
    if (!juliaDisplay) return;
    // Clear previous content
    juliaDisplay.innerHTML = '';
    // Create Julia's container with a colored circle and initial
    const juliaContainer = $84923368ea4a1ab4$var$createCharacterDisplay('Julia', 120, false);
    // Create Julia's name badge
    const juliaName = $84923368ea4a1ab4$var$createNameBadge('Julia', true);
    // Add elements to Julia display
    juliaDisplay.appendChild(juliaContainer);
    juliaDisplay.appendChild(juliaName);
}
// Render Bingo, positioned inline with Bluey
function $84923368ea4a1ab4$export$cd5f809e84a70070() {
    // Get the opponent slot for Bingo
    const bingoSlot = document.getElementById('opponent-bingo');
    if (!bingoSlot) return;
    // Get the bingo display for cleanup
    const bingoDisplay = document.getElementById('bingo-display');
    if (bingoDisplay) {
        // First, make sure to clean up the standalone bingo display to avoid duplicates
        bingoDisplay.innerHTML = '';
        bingoDisplay.style.display = 'none';
    }
    // Only populate Bingo slot if game has at least 3 players (or we're not in a game yet)
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isGameStarted && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length < 3) {
        // Hide Bingo completely when less than 3 players
        bingoSlot.innerHTML = '';
        bingoSlot.style.visibility = 'hidden';
        return;
    } else // Make sure it's visible
    bingoSlot.style.visibility = 'visible';
    // Clear previous content
    bingoSlot.innerHTML = '';
    // Create a div to hold Bingo's image and cards (standard opponent structure)
    const bingoOpponent = document.createElement('div');
    bingoOpponent.className = 'opponent';
    // Create Bingo's container with a colored circle and initial - use 80px to match other opponents
    const bingoContainer = $84923368ea4a1ab4$var$createCharacterDisplay('Bingo', 80);
    bingoOpponent.appendChild(bingoContainer); // No name badge
    // Get Bingo's player data if game is started
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isGameStarted && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length >= 3) {
        // Find Bingo in the players array
        const bingoPlayer = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.find((player)=>player.name === 'Bingo');
        if (bingoPlayer) {
            // Add cards container to show visual representation of Bingo's cards
            const cardsContainer = $84923368ea4a1ab4$var$createCardsContainer(bingoPlayer);
            bingoOpponent.appendChild(cardsContainer);
            // Add UNO indicator if needed
            if (bingoPlayer.hand.length === 1 && bingoPlayer.hasCalledUno) {
                const unoIndicator = document.createElement('div');
                unoIndicator.className = 'uno-indicator';
                unoIndicator.textContent = 'UNO!';
                bingoOpponent.appendChild(unoIndicator);
            }
            // Highlight if it's Bingo's turn
            if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.indexOf(bingoPlayer)) bingoOpponent.classList.add('current-player');
        }
    }
    // Add the opponent to the slot
    bingoSlot.appendChild(bingoOpponent);
}
// Render Dad at the right position
function $84923368ea4a1ab4$export$7e528ffc4843bd4f() {
    // Get the opponent slot for Dad
    const dadSlot = document.getElementById('opponent-bandit');
    if (!dadSlot) return;
    // Get the dad display for cleanup
    const dadDisplay = document.getElementById('dad-display');
    if (dadDisplay) {
        // First, make sure to clean up the standalone dad display to avoid duplicates
        dadDisplay.innerHTML = '';
        dadDisplay.style.display = 'none';
    }
    // Only populate Dad slot if game has 4 players (or we're not in a game yet)
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isGameStarted && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length < 4) {
        // Hide Dad completely when less than 4 players
        dadSlot.innerHTML = '';
        dadSlot.style.visibility = 'hidden';
        return;
    } else // Make sure it's visible
    dadSlot.style.visibility = 'visible';
    // Clear previous content
    dadSlot.innerHTML = '';
    // Create a div to hold Dad's image and cards (standard opponent structure)
    const dadOpponent = document.createElement('div');
    dadOpponent.className = 'opponent';
    // Create Dad's container with a colored circle and initial - use 80px to match other opponents
    const dadContainer = $84923368ea4a1ab4$var$createCharacterDisplay('Dad', 80);
    dadOpponent.appendChild(dadContainer); // No name badge
    // Get Dad's player data if game is started
    if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).isGameStarted && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players && (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.length >= 4) {
        // Find Dad in the players array
        const dadPlayer = (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.find((player)=>player.name === 'Dad');
        if (dadPlayer) {
            // Add cards container to show visual representation of Dad's cards
            const cardsContainer = $84923368ea4a1ab4$var$createCardsContainer(dadPlayer);
            dadOpponent.appendChild(cardsContainer);
            // Add UNO indicator if needed
            if (dadPlayer.hand.length === 1 && dadPlayer.hasCalledUno) {
                const unoIndicator = document.createElement('div');
                unoIndicator.className = 'uno-indicator';
                unoIndicator.textContent = 'UNO!';
                dadOpponent.appendChild(unoIndicator);
            }
            // Highlight if it's Dad's turn
            if ((0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).currentPlayerIndex === (0, $4ee2b06a9c1953f0$export$8e361a1ffbebf172).players.indexOf(dadPlayer)) dadOpponent.classList.add('current-player');
        }
    }
    // Add the opponent to the slot
    dadSlot.appendChild(dadOpponent);
    // Mark Dad as displayed to prevent duplication
    document.body.dataset.dadDisplayed = "true";
}
// Show welcome screen on first load
function $84923368ea4a1ab4$export$cea7e42d5654180f() {
    // Create welcome screen container
    const welcomeScreen = document.createElement('div');
    welcomeScreen.className = 'welcome-screen';
    // Add content
    const content = document.createElement('div');
    content.className = 'welcome-content';
    // Title
    const title = document.createElement('h1');
    title.textContent = 'Bluey UNO!';
    title.style.fontSize = '50px';
    title.style.color = 'white';
    title.style.textShadow = '0 2px 5px rgba(0, 0, 0, 0.5)';
    title.style.marginBottom = '20px';
    // Description
    const description = document.createElement('p');
    description.textContent = 'Play UNO with Bluey and friends!';
    description.style.fontSize = '24px';
    description.style.marginBottom = '40px';
    description.style.color = 'white';
    // Player count selection
    const playerSelection = document.createElement('div');
    playerSelection.style.marginBottom = '30px';
    playerSelection.style.textAlign = 'center';
    const playerCountLabel = document.createElement('div');
    playerCountLabel.textContent = 'Choose number of players:';
    playerCountLabel.style.fontSize = '22px';
    playerCountLabel.style.color = 'white';
    playerCountLabel.style.marginBottom = '15px';
    const playerButtons = document.createElement('div');
    playerButtons.style.display = 'flex';
    playerButtons.style.justifyContent = 'center';
    playerButtons.style.gap = '15px';
    // Create buttons for 2-4 players
    for(let i = 2; i <= 4; i++){
        const button = document.createElement('button');
        button.textContent = i;
        button.style.width = '60px';
        button.style.height = '60px';
        button.style.borderRadius = '50%';
        button.style.border = 'none';
        button.style.backgroundColor = 'white';
        button.style.color = '#ff5757';
        button.style.fontSize = '28px';
        button.style.fontWeight = 'bold';
        button.style.cursor = 'pointer';
        button.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.3)';
        button.style.fontFamily = "'Comic Sans MS', 'Comic Sans', cursive, sans-serif";
        // Add hover effect
        button.onmouseenter = ()=>{
            button.style.transform = 'scale(1.1)';
            button.style.backgroundColor = '#ffcc66';
        };
        button.onmouseleave = ()=>{
            button.style.transform = 'scale(1)';
            button.style.backgroundColor = 'white';
        };
        // Start game with selected number of players
        button.onclick = ()=>{
            document.body.removeChild(welcomeScreen);
            (0, $4ee2b06a9c1953f0$export$5f9f2982725d71d5)(i);
        };
        playerButtons.appendChild(button);
    }
    playerSelection.appendChild(playerCountLabel);
    playerSelection.appendChild(playerButtons);
    // Build content
    content.appendChild(title);
    content.appendChild(description);
    content.appendChild(playerSelection);
    welcomeScreen.appendChild(content);
    // Style welcome screen
    welcomeScreen.style.position = 'fixed';
    welcomeScreen.style.top = '0';
    welcomeScreen.style.left = '0';
    welcomeScreen.style.width = '100%';
    welcomeScreen.style.height = '100%';
    welcomeScreen.style.backgroundColor = '#ff5757';
    welcomeScreen.style.display = 'flex';
    welcomeScreen.style.justifyContent = 'center';
    welcomeScreen.style.alignItems = 'center';
    welcomeScreen.style.zIndex = '9999';
    welcomeScreen.style.fontFamily = "'Comic Sans MS', 'Comic Sans', cursive, sans-serif";
    // Add decorative Uno cards floating in background
    for(let i = 0; i < 20; i++){
        const card = document.createElement('div');
        const size = Math.random() * 50 + 20; // Random size between 20-70px
        const xPos = Math.random() * 100; // Random horizontal position
        const yPos = Math.random() * 100; // Random vertical position
        const rotation = Math.random() * 360; // Random rotation
        const delay = Math.random() * 10; // Random animation delay
        card.style.position = 'absolute';
        card.style.width = `${size}px`;
        card.style.height = `${size * 1.4}px`;
        card.style.borderRadius = '5px';
        card.style.left = `${xPos}%`;
        card.style.top = `${yPos}%`;
        card.style.transform = `rotate(${rotation}deg)`;
        // Random color
        const colors = [
            '#4a7af5',
            '#47c83e',
            '#ffee3e',
            '#ff3b3b'
        ];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];
        card.style.backgroundColor = randomColor;
        card.style.border = '2px solid white';
        card.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
        // Add floating animation
        card.style.animation = `float ${Math.random() * 10 + 10}s ease-in-out infinite`;
        card.style.animationDelay = `${delay}s`;
        welcomeScreen.appendChild(card);
    }
    // Add to body
    document.body.appendChild(welcomeScreen);
    // Add global styles for animations
    const styleElement = document.createElement('style');
    styleElement.textContent = `
    @keyframes float {
      0% { transform: translate(0, 0) rotate(${Math.random() * 360}deg); }
      50% { transform: translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px) rotate(${Math.random() * 360}deg); }
      100% { transform: translate(0, 0) rotate(${Math.random() * 360}deg); }
    }
  `;
    document.head.appendChild(styleElement);
}
// Initialize the game when the page loads
window.addEventListener('DOMContentLoaded', ()=>{
    // Hide any buttons that might be leftover from previous implementation
    const sayUnoButton = document.getElementById('say-uno');
    const toggleSoundButton = document.getElementById('toggle-sound');
    const drawCardButton = document.getElementById('draw-card');
    if (sayUnoButton) sayUnoButton.style.display = 'none';
    if (toggleSoundButton) toggleSoundButton.style.display = 'none';
    if (drawCardButton) drawCardButton.style.display = 'none';
    // Initialize empty starter display
    (0, $4ee2b06a9c1953f0$export$b93b445bb1e5ae81)();
    $84923368ea4a1ab4$export$37af8f2807572b25();
    // Render Julia character
    $84923368ea4a1ab4$var$renderJulia();
    // Bingo and Dad are now rendered as part of the renderOpponents function
    // Add welcome screen
    $84923368ea4a1ab4$export$cea7e42d5654180f();
    // Add class to body when in standalone mode (added to homescreen)
    if (window.navigator.standalone) document.body.classList.add('standalone');
    // Add event listener for invalid card play
    window.addEventListener('invalidCardPlay', (event)=>{
        // Get the card index from the event
        const cardIndex = event.detail.cardIndex;
        const errorMessage = event.detail.message;
        // Find the card element in the player's hand
        const playerHand = document.getElementById('player-hand');
        const cardsContainer = playerHand.querySelector('.player-cards');
        const cardElements = cardsContainer.querySelectorAll('.card');
        if (cardElements[cardIndex]) {
            // Add shake animation
            const cardElement = cardElements[cardIndex];
            cardElement.classList.add('shake');
            // Show error message if provided (specifically for Wild Draw 4 restriction)
            if (errorMessage) $84923368ea4a1ab4$export$f3b8e31d958a9f24(errorMessage);
            // Remove the shake class after animation completes
            setTimeout(()=>{
                cardElement.classList.remove('shake');
            }, 500);
        }
    });
    // Add event listener for invalid draw (when player has playable cards)
    window.addEventListener('invalidDraw', ()=>{
        // Find the deck element
        const deckElement = document.getElementById('deck');
        if (deckElement) {
            // Add shake animation
            deckElement.classList.add('shake');
            // Remove the shake class after animation completes
            setTimeout(()=>{
                deckElement.classList.remove('shake');
            }, 500);
        }
    });
});
// Function to show an error message to the player
function $84923368ea4a1ab4$export$f3b8e31d958a9f24(message) {
    // Create or get the error message container
    let errorContainer = document.getElementById('error-message-container');
    if (!errorContainer) {
        errorContainer = document.createElement('div');
        errorContainer.id = 'error-message-container';
        $84923368ea4a1ab4$var$applyStyles(errorContainer, {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(255, 0, 0, 0.8)',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            maxWidth: '80%',
            textAlign: 'center',
            zIndex: '1000',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            fontSize: '20px',
            fontWeight: 'bold'
        });
        document.body.appendChild(errorContainer);
    }
    // Set the message
    errorContainer.textContent = message;
    // Show and then hide after a delay
    errorContainer.style.display = 'block';
    setTimeout(()=>{
        errorContainer.style.display = 'none';
    }, 3000);
}
// Draw requirement messages removed per issue #38
// Event listeners for drawing requirements removed
// (keeping empty commented handlers to document the available events)
window.addEventListener('showDrawRequirement', (event)=>{
// Visual notifications removed per issue #38
});
window.addEventListener('drawRequirementComplete', (event)=>{
// Visual notifications removed per issue #38
});


// Game rules module for Bluey Uno
// Constants for the Bluey Uno game
// Card colors
const $5cdc329eeebd56d0$export$6702b1e9c2005940 = Object.freeze({
    RED: 'red',
    BLUE: 'blue',
    GREEN: 'green',
    YELLOW: 'yellow',
    WILD: 'wild'
});
const $5cdc329eeebd56d0$export$3e6c5e426af162f8 = Object.freeze({
    NUMBER: 'number',
    SPECIAL: 'special',
    WILD: 'wild'
});
const $5cdc329eeebd56d0$export$801f53f8ff768e98 = Object.freeze({
    NUMBERS: [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9'
    ],
    SPECIAL: {
        SKIP: 'Skip',
        REVERSE: 'Reverse',
        DRAW_TWO: 'Draw 2'
    },
    WILD: {
        STANDARD: 'Wild',
        DRAW_FOUR: 'Wild Draw 4'
    }
});
const $5cdc329eeebd56d0$export$450e1b0c9f407e0a = Object.freeze({
    // Numbers - using the emoji faces 
    '0': "\uD83D\uDE36",
    '1': "\uD83D\uDE2C",
    '2': "\uD83D\uDE01",
    '3': "\uD83D\uDE0D",
    '4': "\uD83D\uDE07",
    '5': "\uD83D\uDE0E",
    '6': "\uD83D\uDE42",
    '7': "\uD83D\uDE0A",
    '8': "\uD83D\uDE18",
    '9': "\uD83D\uDE20",
    // Special cards
    'Skip': "\uD83D\uDE45\u200D\u2640\uFE0F",
    'Reverse': "\uD83D\uDC49",
    'Draw 2': "\uD83D\uDC6F\u200D\u2640\uFE0F",
    'Wild': "\uD83D\uDC4D",
    'Wild Draw 4': "\uD83D\uDCA9"
});
const $5cdc329eeebd56d0$export$abbf5b8b5f599cc4 = Object.freeze({
    PLAYER: 'Julia',
    AI: {
        BLUEY: 'Bluey',
        BINGO: 'Bingo',
        DAD: 'Dad',
        BANDIT: 'Bandit'
    }
});
const $5cdc329eeebd56d0$export$8189da1ed3856d00 = [
    $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BLUEY,
    $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BINGO,
    $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.DAD
];
const $5cdc329eeebd56d0$export$14a8dc898c9128c1 = Object.freeze({
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.PLAYER]: '#FFCC66',
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BLUEY]: '#1E90FF',
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BINGO]: '#FF6B6B',
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BANDIT]: '#4169E1',
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.DAD]: '#5F4B32' // Brown
});
const $5cdc329eeebd56d0$export$3583ecc5c025ec1a = Object.freeze({
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.PLAYER]: {
        name: $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.PLAYER,
        color: $5cdc329eeebd56d0$export$14a8dc898c9128c1[$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.PLAYER],
        imagePath: 'public/images/julia.png',
        elementId: 'julia-img',
        initial: 'J',
        textColor: '#333'
    },
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BLUEY]: {
        name: $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BLUEY,
        color: $5cdc329eeebd56d0$export$14a8dc898c9128c1[$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BLUEY],
        imagePath: 'public/images/bluey.png',
        elementId: 'bluey-img',
        initial: 'B',
        textColor: 'white',
        soundFile: 'bluey.mp3'
    },
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BINGO]: {
        name: $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BINGO,
        color: $5cdc329eeebd56d0$export$14a8dc898c9128c1[$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BINGO],
        imagePath: 'public/images/bingo.png',
        elementId: 'bingo-img',
        initial: 'B',
        textColor: 'white',
        soundFile: 'bingo.mp3'
    },
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.DAD]: {
        name: $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.DAD,
        color: $5cdc329eeebd56d0$export$14a8dc898c9128c1[$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.DAD],
        imagePath: 'public/images/dad.png',
        elementId: 'dad-img',
        initial: 'D',
        textColor: 'white',
        soundFile: 'dad.mp3'
    },
    [$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BANDIT]: {
        name: $5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BANDIT,
        color: $5cdc329eeebd56d0$export$14a8dc898c9128c1[$5cdc329eeebd56d0$export$abbf5b8b5f599cc4.AI.BANDIT],
        imagePath: 'public/images/bandit.png',
        elementId: 'bandit-img',
        initial: 'B',
        textColor: 'white'
    }
});
const $5cdc329eeebd56d0$export$cd5f5ab4e56c9e8e = Object.freeze({
    CARD_PLAY: {
        type: 'synthesized',
        waveform: 'sine',
        frequency: 440,
        duration: 0.3
    },
    YOUR_TURN: {
        type: 'synthesized',
        notes: [
            {
                freq: 523.25,
                start: 0,
                duration: 0.5
            },
            {
                freq: 659.25,
                start: 0.2,
                duration: 0.6
            }
        ]
    },
    UNO_CALL: {
        type: 'synthesized'
    },
    WIN: {
        type: 'synthesized'
    },
    BINGO: {
        type: 'sample',
        file: 'audio/bingo.mp3',
        elementId: 'bingo-sound-preload'
    },
    BLUEY: {
        type: 'sample',
        file: 'audio/bluey.mp3',
        elementId: 'bluey-sound-preload'
    },
    DAD: {
        type: 'sample',
        file: 'audio/dad.mp3',
        elementId: 'dad-sound-preload'
    }
});
const $5cdc329eeebd56d0$export$f9e3cd753a17f06a = Object.freeze({
    RED: '#ff3b3b',
    BLUE: '#4a7af5',
    GREEN: '#47c83e',
    YELLOW: '#ffee3e',
    BACKGROUND_PINK: '#ffdbec',
    CONTAINER_PINK: '#ffc0dc'
});



/**
 * Checks if a card can be played on top of the current discard
 * @param {Object} card - The card to be played
 * @param {Object} topDiscard - The top card of the discard pile
 * @param {string} currentColor - The current active color
 * @param {Array} playerHand - The hand of the player attempting to play the card
 * @returns {boolean} Whether the card can be played
 */ function $c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62(card, topDiscard, currentColor, playerHand) {
    // Regular Wild cards can always be played
    if (card.value === (0, $5cdc329eeebd56d0$export$801f53f8ff768e98).WILD.STANDARD) return true;
    // Wild Draw 4 can only be played if the player has no cards matching the current color
    if (card.value === (0, $5cdc329eeebd56d0$export$801f53f8ff768e98).WILD.DRAW_FOUR) {
        // If playerHand is provided (for validation), check if player has playable cards
        if (playerHand && topDiscard) {
            // Check if player has any cards matching the current color
            const hasPlayableCard = playerHand.some((handCard)=>handCard !== card && (handCard.color === currentColor || // Matching color
                handCard.value === topDiscard.value && handCard.value !== (0, $5cdc329eeebd56d0$export$801f53f8ff768e98).WILD.STANDARD && handCard.value !== (0, $5cdc329eeebd56d0$export$801f53f8ff768e98).WILD.DRAW_FOUR // Matching value (excluding wilds)
                ));
            // Wild Draw 4 can only be played if player has no playable cards
            return !hasPlayableCard;
        }
        // If no hand is provided or no top discard (for backward compatibility), allow the play
        return true;
    }
    // If there's no discard pile yet, any card can be played
    if (!topDiscard) return true;
    // Card can be played if it matches the current color
    if (card.color === currentColor) return true;
    // Card can be played if it matches the value of the top card
    if (card.value === topDiscard.value) return true;
    return false;
}
/**
 * Check if player has any legal moves
 * @param {Array} hand - The player's hand
 * @param {Object} topDiscard - The top card of the discard pile
 * @param {string} currentColor - The current active color
 * @returns {boolean} Whether the player has any legal moves
 */ function $c80abe9c5b3e3d60$export$c7fe003a8301344f(hand, topDiscard, currentColor) {
    // Check each card in hand to see if any can be played
    return hand.some((card)=>$c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62(card, topDiscard, currentColor, hand));
}
/**
 * Choose the most strategic color for AI
 * @param {Array} hand - AI player's hand
 * @returns {string} The chosen color
 */ function $c80abe9c5b3e3d60$export$1847a034464ce353(hand) {
    const colorCounts = {
        red: 0,
        blue: 0,
        green: 0,
        yellow: 0
    };
    // Count colors in hand
    hand.forEach((card)=>{
        if (card.color !== 'wild') colorCounts[card.color]++;
    });
    // Find most frequent color
    let maxCount = 0;
    let chosenColor = 'red'; // Default if no colored cards in hand
    for(const color in colorCounts)if (colorCounts[color] > maxCount) {
        maxCount = colorCounts[color];
        chosenColor = color;
    }
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`AI chooses color: ${chosenColor}`);
    return chosenColor;
}
/**
 * Determine if the game has been won
 * @param {Object} player - Player to check for win condition
 * @returns {boolean} True if player has won
 */ function $c80abe9c5b3e3d60$export$550a2255ae2641ad(player) {
    return player.hand.length === 0;
}


// Special card effects module for Bluey Uno


// AI player logic for Bluey Uno


/**
 * Determines if an AI player can make a move in the current game state
 * @param {Object} gameState - Current game state
 * @returns {boolean} Whether AI can make a move
 */ function $a9aa67ee0388acd1$var$canAIMakeMove(gameState) {
    // Safety check - this function should only be called for AI players
    if (gameState.currentPlayerIndex === 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("ERROR: AI turn called for human player (index 0)", 'error');
        return false;
    }
    // If ANY player is currently drawing cards, pause the AI until drawing is complete
    if (gameState.isDrawingCards || gameState.requiredDraws > 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("AI paused - waiting for player to finish drawing cards...");
        return false;
    }
    // Reset drawing flag if it was mistakenly left on for AI players
    if (gameState.isDrawingCards && gameState.currentPlayerIndex !== 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("Resetting drawing flag for AI player");
        gameState.isDrawingCards = false;
    }
    // Add an extra safety check for game state
    if (!gameState.isGameStarted) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("ERROR: Attempted to play AI turn when game is not started", 'error');
        return false;
    }
    return true;
}
/**
 * Finds the best card for AI to play from their hand
 * @param {Array} hand - AI player's hand
 * @param {Object} topDiscard - Top card on discard pile
 * @param {string} currentColor - Current active color
 * @returns {Object} Object with cardIndex or null, and whether card was found
 */ function $a9aa67ee0388acd1$var$findBestCardToPlay(hand, topDiscard, currentColor) {
    // Simple strategy: play the first playable card
    // Could be enhanced with more sophisticated AI logic later
    for(let i = 0; i < hand.length; i++){
        const card = hand[i];
        if ((0, $c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62)(card, topDiscard, currentColor, hand)) {
            (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`AI found playable card: ${card.color} ${card.value} at index ${i}`);
            return {
                cardIndex: i,
                found: true
            };
        }
    }
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)("AI found no playable cards in hand");
    return {
        cardIndex: null,
        found: false
    };
}
/**
 * Determines AI strategy when drawing a card
 * @param {Object} drawnCard - The card that was drawn
 * @param {Object} topDiscard - Top card on discard pile 
 * @param {string} currentColor - Current active color
 * @param {Array} hand - AI player's hand (including the drawn card)
 * @returns {Object} Strategy object with action and cardIndex
 */ function $a9aa67ee0388acd1$var$getDrawCardStrategy(drawnCard, topDiscard, currentColor, hand) {
    // Check if drawn card can be played
    if ((0, $c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62)(drawnCard, topDiscard, currentColor, hand)) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("AI can play the drawn card");
        return {
            action: 'play',
            cardIndex: hand.length - 1 // Drawn card is at the end of hand
        };
    } else {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("AI cannot play the drawn card - will pass turn");
        return {
            action: 'pass',
            cardIndex: null
        };
    }
}
function $a9aa67ee0388acd1$export$fec51735aed2df57(gameState, dependencies) {
    const { playCard: playCard, drawSingleCard: drawSingleCard, nextTurn: nextTurn, updateGameDisplay: updateGameDisplay } = dependencies;
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`AI turn starting. Current player: ${gameState.currentPlayerIndex}`);
    // Check if AI can make a move
    if (!$a9aa67ee0388acd1$var$canAIMakeMove(gameState)) return {
        action: 'blocked',
        reason: 'Cannot make move in current state'
    };
    const player = gameState.players[gameState.currentPlayerIndex];
    const topDiscard = gameState.discardPile[gameState.discardPile.length - 1];
    // First, try to find a card to play from hand
    const playResult = $a9aa67ee0388acd1$var$findBestCardToPlay(player.hand, topDiscard, gameState.currentColor);
    if (playResult.found) {
        // Play the card with a delay for better UX
        setTimeout(()=>{
            playCard(gameState, gameState.currentPlayerIndex, playResult.cardIndex);
        }, 500);
        return {
            action: 'play',
            cardIndex: playResult.cardIndex
        };
    }
    // If no card can be played, draw a card
    const drawnCard = drawSingleCard(gameState.currentPlayerIndex);
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`AI drew: ${drawnCard.color} ${drawnCard.value}`);
    // Determine strategy for the drawn card
    const drawStrategy = $a9aa67ee0388acd1$var$getDrawCardStrategy(drawnCard, topDiscard, gameState.currentColor, player.hand);
    if (drawStrategy.action === 'play') {
        // Play the drawn card with a delay
        setTimeout(()=>{
            playCard(gameState, gameState.currentPlayerIndex, drawStrategy.cardIndex);
        }, 500);
        return {
            action: 'draw_and_play',
            cardIndex: drawStrategy.cardIndex
        };
    } else {
        // Cannot play drawn card, pass turn
        nextTurn();
        updateGameDisplay(gameState);
        // If it's still AI's turn, continue (but only if no player is drawing cards)
        if (gameState.currentPlayerIndex !== 0 && !gameState.isDrawingCards) setTimeout(()=>$a9aa67ee0388acd1$export$b5e0e64c49f4a673(gameState, dependencies), 1000);
        return {
            action: 'draw_and_pass'
        };
    }
}
function $a9aa67ee0388acd1$export$b5e0e64c49f4a673(gameState, dependencies) {
    return $a9aa67ee0388acd1$export$fec51735aed2df57(gameState, dependencies);
}


/**
 * Handle Skip card effect
 * @param {Object} gameState - Current game state
 * @param {Function} updateGameDisplay - Function to update the UI
 * @returns {Object} Effect result with skipNextTurn flag
 */ function $9e99b98ae16147e3$var$handleSkipCard(gameState, updateGameDisplay) {
    const skippedPlayerIndex = (0, $820877bea407f5fe$export$62dd595d6a764af5)(gameState.currentPlayerIndex, gameState.direction, gameState.players.length);
    const skippedPlayerName = gameState.players[skippedPlayerIndex].name;
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`${skippedPlayerName}'s turn is skipped`);
    gameState.currentPlayerIndex = skippedPlayerIndex;
    return {
        skipNextTurn: false
    }; // Let normal turn advancement happen
}
/**
 * Handle Reverse card effect
 * @param {Object} gameState - Current game state
 * @param {Function} updateGameDisplay - Function to update the UI
 * @returns {Object} Effect result with skipNextTurn flag
 */ function $9e99b98ae16147e3$var$handleReverseCard(gameState, updateGameDisplay) {
    // Reverse direction
    gameState.direction *= -1;
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`Direction reversed: now playing ${gameState.direction === 1 ? 'clockwise' : 'counter-clockwise'}`);
    // Make sure UI updates when direction changes
    updateGameDisplay(gameState);
    // In a 2-player game, reverse acts like skip
    if (gameState.players.length === 2) {
        gameState.currentPlayerIndex = (0, $820877bea407f5fe$export$62dd595d6a764af5)(gameState.currentPlayerIndex, gameState.direction, gameState.players.length);
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("2-player game: Reverse acts like Skip");
    }
    return {
        skipNextTurn: false
    }; // Let normal turn advancement happen
}
/**
 * Handle Draw 2 card effect
 * @param {Object} gameState - Current game state
 * @param {Function} handleDrawCards - Function to handle drawing cards
 * @param {Function} handleSkipNextPlayer - Function to skip next player
 * @param {Function} updateGameDisplay - Function to update the UI
 * @returns {Object} Effect result with skipNextTurn flag
 */ function $9e99b98ae16147e3$var$handleDraw2Card(gameState, handleDrawCards, handleSkipNextPlayer, updateGameDisplay, dependencies) {
    // Next player draws 2 cards and skips their turn
    const nextPlayerIndex = handleDrawCards(2);
    // Check for AI-to-AI drawing situation
    const isCurrentPlayerAI = gameState.players[gameState.currentPlayerIndex].isAI;
    const isNextPlayerAI = gameState.players[nextPlayerIndex].isAI;
    const isAItoAIDrawing = isCurrentPlayerAI && isNextPlayerAI;
    // Handle AI and human players differently:
    if (isNextPlayerAI) {
        // For AI, skip their turn immediately since they've already drawn
        handleSkipNextPlayer(nextPlayerIndex);
        // For AI-to-AI interactions, add a small delay to prevent freezing
        if (isAItoAIDrawing) // Allow a small delay for UI updates
        setTimeout(()=>{
            // Continue with the normal game flow after delay
            updateGameDisplay(gameState);
            // Let the next AI player take their turn
            if (gameState.currentPlayerIndex !== 0) {
                const aiDependencies = {
                    playCard: dependencies.playCard,
                    drawSingleCard: dependencies.drawSingleCard,
                    nextTurn: dependencies.nextTurn,
                    updateGameDisplay: dependencies.updateGameDisplay
                };
                setTimeout(()=>(0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673)(gameState, aiDependencies), 500);
            }
        }, 300);
        // Since we manually called handleSkipNextPlayer, we should skip the normal nextTurn()
        return {
            skipNextTurn: true
        };
    } else // For human players, we pause the game until they draw all required cards
    // Don't advance the turn yet - it will happen after they finish drawing
    return {
        skipNextTurn: true
    };
}
/**
 * Handle Wild card effect
 * @param {Object} gameState - Current game state
 * @returns {Object} Effect result with skipNextTurn flag
 */ function $9e99b98ae16147e3$var$handleWildCard(gameState) {
    // For AI player, choose color based on hand
    if (gameState.currentPlayerIndex !== 0) {
        const player = gameState.players[gameState.currentPlayerIndex];
        const chosenColor = (0, $c80abe9c5b3e3d60$export$1847a034464ce353)(player.hand);
        gameState.currentColor = chosenColor;
        gameState.waitingForColorChoice = false;
    }
    // For human player, color choice UI is handled elsewhere
    return {
        skipNextTurn: false
    }; // Let normal turn advancement happen
}
/**
 * Handle Wild Draw 4 card effect
 * @param {Object} gameState - Current game state
 * @param {Function} getNextPlayerIndex - Function to get next player index
 * @param {Function} handleDrawCards - Function to handle drawing cards
 * @param {Function} handleSkipNextPlayer - Function to skip next player
 * @param {Function} updateGameDisplay - Function to update the UI
 * @returns {Object} Effect result with skipNextTurn flag
 */ function $9e99b98ae16147e3$var$handleWildDraw4Card(gameState, getNextPlayerIndexFn, handleDrawCards, handleSkipNextPlayer, updateGameDisplay, dependencies) {
    // For Wild Draw 4, the player who played it MUST choose color first
    // For human players, we already handle this in continueAfterCardPlay
    // For AI players, we need to handle the color selection here
    if (gameState.players[gameState.currentPlayerIndex].isAI) {
        // AI chooses color based on their hand
        const player = gameState.players[gameState.currentPlayerIndex];
        const chosenColor = (0, $c80abe9c5b3e3d60$export$1847a034464ce353)(player.hand);
        gameState.currentColor = chosenColor;
        gameState.waitingForColorChoice = false;
        // Store the next player index who will draw (important: do this BEFORE changing turns)
        gameState.pendingDrawPlayerIndex = getNextPlayerIndexFn();
        gameState.pendingDrawCount = 4;
        // Next player draws 4 cards and loses turn
        const nextIdx = handleDrawCards(4);
        // Check for AI-to-AI drawing situation
        const isCurrentPlayerAI_WD4 = gameState.players[gameState.currentPlayerIndex].isAI;
        const isNextPlayerAI_WD4 = gameState.players[nextIdx].isAI;
        const isAItoAIDrawing_WD4 = isCurrentPlayerAI_WD4 && isNextPlayerAI_WD4;
        // Handle AI and human players differently:
        if (isNextPlayerAI_WD4) {
            // For AI, skip their turn immediately since they've already drawn
            handleSkipNextPlayer(nextIdx);
            // For AI-to-AI interactions, add a small delay to prevent freezing
            if (isAItoAIDrawing_WD4) // Allow a small delay for UI updates
            setTimeout(()=>{
                // Continue with the normal game flow after delay
                updateGameDisplay(gameState);
                // Let the next AI player take their turn
                if (gameState.currentPlayerIndex !== 0) {
                    const aiDependencies = {
                        playCard: dependencies.playCard,
                        drawSingleCard: dependencies.drawSingleCard,
                        nextTurn: dependencies.nextTurn,
                        updateGameDisplay: dependencies.updateGameDisplay
                    };
                    setTimeout(()=>(0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673)(gameState, aiDependencies), 500);
                }
            }, 300);
            // Since we manually called handleSkipNextPlayer, we should skip the normal nextTurn()
            return {
                skipNextTurn: true
            };
        } else // For human players, we pause the game until they draw all required cards
        // Don't advance the turn yet - it will happen after they finish drawing
        return {
            skipNextTurn: true
        };
    } else // Human player plays Wild Draw 4
    // We don't handle any draw logic here
    // It will be handled after the player chooses a color in chooseColor()
    return {
        skipNextTurn: true
    }; // Prevent automatic turn advancement
}
function $9e99b98ae16147e3$export$d5d0975972f6aabc(card, gameState, dependencies) {
    const { getNextPlayerIndex: getNextPlayerIndexFn, handleDrawCards: handleDrawCards, handleSkipNextPlayer: handleSkipNextPlayer, updateGameDisplay: updateGameDisplay, playAITurn: playAITurn } = dependencies;
    // Always update the current color to the card's color
    // This fixes the color not updating when playing special cards
    if (card.color !== 'wild') gameState.currentColor = card.color;
    let result = {
        skipNextTurn: false
    };
    switch(card.value){
        case 'Skip':
            result = $9e99b98ae16147e3$var$handleSkipCard(gameState, updateGameDisplay);
            break;
        case 'Reverse':
            result = $9e99b98ae16147e3$var$handleReverseCard(gameState, updateGameDisplay);
            break;
        case 'Draw 2':
            result = $9e99b98ae16147e3$var$handleDraw2Card(gameState, handleDrawCards, handleSkipNextPlayer, updateGameDisplay, dependencies);
            break;
        case 'Wild':
            result = $9e99b98ae16147e3$var$handleWildCard(gameState);
            break;
        case 'Wild Draw 4':
            result = $9e99b98ae16147e3$var$handleWildDraw4Card(gameState, getNextPlayerIndexFn, handleDrawCards, handleSkipNextPlayer, updateGameDisplay, dependencies);
            break;
        default:
            // We already set the current color at the beginning of this function
            // for all non-wild cards, so no additional action is needed here
            result = {
                skipNextTurn: false
            }; // Let normal turn advancement happen
            break;
    }
    // Return a flag indicating if we should skip the normal nextTurn() call
    return result.skipNextTurn;
}



// Helper functions for game logic simplification

function $e04d9f482a55f400$export$8f1857c910e27042(playCard, drawSingleCard, nextTurn, updateGameDisplay) {
    return {
        playCard: playCard,
        drawSingleCard: drawSingleCard,
        nextTurn: nextTurn,
        updateGameDisplay: updateGameDisplay
    };
}
function $e04d9f482a55f400$export$a0fd2a2b4a7af6f(gameState, nextPlayerIndex, handleSkipNextPlayer, updateGameDisplay, playAITurn, aiDependencies) {
    (0, $820877bea407f5fe$export$aaf39ef296ada06c)("Wild Draw 4 - AI player needs to draw and be skipped");
    // For AI players, skip their turn immediately
    handleSkipNextPlayer(nextPlayerIndex);
    // Update the UI immediately
    updateGameDisplay(gameState);
    // If it's AI's turn, let them play after a delay
    if (gameState.currentPlayerIndex !== 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)("Starting next AI turn after Wild Draw 4 color choice and skip");
        setTimeout(()=>playAITurn(gameState, aiDependencies), 1000);
    }
}
function $e04d9f482a55f400$export$2f7cb009dbcbf6ab(gameState, nextTurn, updateGameDisplay, playAITurn, aiDependencies) {
    // For regular Wild cards, just move to next player's turn
    nextTurn();
    // Update the UI
    updateGameDisplay(gameState);
    // If it's AI's turn, let them play after a delay
    if (gameState.currentPlayerIndex !== 0) setTimeout(()=>playAITurn(gameState, aiDependencies), 1000);
}
function $e04d9f482a55f400$export$56f5bc3de7a2569d(drawnCard, topDiscard, currentColor, playerHand, canPlayCard) {
    const isPlayable = canPlayCard(drawnCard, topDiscard, currentColor, playerHand);
    if (isPlayable) (0, $820877bea407f5fe$export$aaf39ef296ada06c)("Drawn card can be played - waiting for player to play it");
    else (0, $820877bea407f5fe$export$aaf39ef296ada06c)("Drawn card can't be played - moving to next player");
    return isPlayable;
}
function $e04d9f482a55f400$export$812129311cdd0390(gameState, requiredDraws, currentPlayerIndex, waitingForColorChoice) {
    // Always allow drawing if the player has required draws
    if (requiredDraws > 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`Allowing draw due to requiredDraws: ${requiredDraws}`);
        return {
            isValid: true,
            reason: 'required_draws'
        };
    }
    // Only human player can use this function when it's their turn
    if (currentPlayerIndex !== 0) {
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)('Blocking draw - not human player\'s turn');
        return {
            isValid: false,
            reason: 'not_human_turn'
        };
    }
    // Don't draw if waiting for color choice
    if (waitingForColorChoice) return {
        isValid: false,
        reason: 'waiting_for_color'
    };
    return {
        isValid: true,
        reason: 'normal_draw'
    };
}
function $e04d9f482a55f400$export$4e804bd8a20f2dcb(gameState, playAITurn, aiDependencies, context = '') {
    if (gameState.currentPlayerIndex !== 0) {
        const logContext = context ? ` ${context}` : '';
        (0, $820877bea407f5fe$export$aaf39ef296ada06c)(`Starting AI turn${logContext}`);
        setTimeout(()=>playAITurn(gameState, aiDependencies), 1000);
    }
}


// Game state
let $4ee2b06a9c1953f0$export$8e361a1ffbebf172 = {
    deck: [],
    discardPile: [],
    players: [],
    currentPlayerIndex: 0,
    direction: 1,
    isGameStarted: false,
    currentColor: null,
    waitingForColorChoice: false,
    pendingDrawPlayerIndex: null,
    pendingDrawCount: 0,
    requiredDraws: 0,
    isDrawingCards: false,
    winningPlayerIndex: undefined // Store the index of the player who won by playing their last card
};
// Create empty deck and discard pile for initial display
function $4ee2b06a9c1953f0$export$b93b445bb1e5ae81() {
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck = [
        {
            color: 'blue',
            value: '0',
            emoji: "\uD83D\uDE36"
        }
    ]; // Just for display
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile = [];
    // Create player with sample cards for initial display
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players = [
        {
            name: 'Julia',
            hand: [
                {
                    color: 'red',
                    value: '7',
                    emoji: "\uD83D\uDE0A"
                },
                {
                    color: 'blue',
                    value: '4',
                    emoji: "\uD83D\uDE07"
                },
                {
                    color: 'green',
                    value: '2',
                    emoji: "\uD83D\uDE01"
                },
                {
                    color: 'yellow',
                    value: '9',
                    emoji: "\uD83D\uDE20"
                },
                {
                    color: 'wild',
                    value: 'Wild',
                    emoji: "\uD83C\uDF08"
                }
            ],
            isAI: false,
            hasCalledUno: false
        }
    ];
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isGameStarted = false;
}
// Start the game
function $4ee2b06a9c1953f0$export$5f9f2982725d71d5(numPlayers = 2) {
    // Initialize sound system if it's the first game
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).initialize();
    // Create and shuffle the deck
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck = (0, $125bb8bfc3b37dfd$export$ea1002f91841a9fc)();
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck = (0, $125bb8bfc3b37dfd$export$2948b794f685d50b)($4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck);
    // Create players (1 human, 1-3 AI)
    // Ensure numPlayers is between 2 and 4 (1 human + 1-3 AI)
    const totalPlayers = Math.max(2, Math.min(4, numPlayers));
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players = (0, $d2949551220fa082$export$94a3c07841793e28)(totalPlayers);
    // Deal cards
    (0, $125bb8bfc3b37dfd$export$bc03ba615e47b5f3)($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck, 7);
    // Put one card on discard pile
    $4ee2b06a9c1953f0$var$placeInitialCard();
    // Set game as started
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isGameStarted = true;
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = 0; // Human player goes first
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.winningPlayerIndex = undefined; // Reset winning player
    // Update UI
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
    // Play "your turn" sound since the player goes first
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('yourTurn');
    // If it's not the player's turn (index 0), let AI make a move
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex !== 0) {
        const aiDependencies = {
            playCard: $4ee2b06a9c1953f0$export$317a6377ae525e39,
            drawSingleCard: $4ee2b06a9c1953f0$var$drawSingleCard,
            nextTurn: $4ee2b06a9c1953f0$var$nextTurn,
            updateGameDisplay: $84923368ea4a1ab4$export$3f43ff7bf5d42a53
        };
        setTimeout(()=>(0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673)($4ee2b06a9c1953f0$export$8e361a1ffbebf172, aiDependencies), 1000);
    }
}
function $4ee2b06a9c1953f0$var$placeInitialCard() {
    // Get a card from the deck that is not a special card
    let initialCardIndex;
    do initialCardIndex = Math.floor(Math.random() * $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck.length);
    while ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck[initialCardIndex].value === 'Skip' || $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck[initialCardIndex].value === 'Reverse' || $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck[initialCardIndex].value === 'Draw 2' || $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck[initialCardIndex].value === 'Wild' || $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck[initialCardIndex].value === 'Wild Draw 4');
    // Move the card from deck to discard pile
    const initialCard = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck.splice(initialCardIndex, 1)[0];
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile.push(initialCard);
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentColor = initialCard.color;
}
// Player plays a card
function $4ee2b06a9c1953f0$export$317a6377ae525e39(gameState, playerIndex, cardIndex) {
    // If any player is currently drawing cards, don't allow ANY actions
    // This completely pauses the game until the drawing is complete
    if (gameState.isDrawingCards || gameState.requiredDraws > 0) {
        // AI players should never try to play while isDrawingCards is true
        // But if they do, just silently prevent it
        // Show a message if it's a human player trying to play during drawing
        if (playerIndex === 0) window.dispatchEvent(new CustomEvent('invalidCardPlay', {
            detail: {
                cardIndex: cardIndex,
                message: "You must finish drawing all required cards first!"
            }
        }));
        console.log("Prevented card play while drawing cards in progress");
        return; // Exit early, don't proceed with card play
    }
    const player = gameState.players[playerIndex];
    const card = player.hand[cardIndex];
    // Handle case when there's no discard pile yet
    const topDiscard = gameState.discardPile.length > 0 ? gameState.discardPile[gameState.discardPile.length - 1] : null;
    // Check if card can be played (pass the player's hand for Wild Draw 4 validation)
    if (!(0, $c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62)(card, topDiscard, gameState.currentColor, player.hand)) {
        // If human player tries to play an invalid card, trigger shake animation
        if (playerIndex === 0) // For Wild Draw 4, provide specific feedback if player has playable cards
        window.dispatchEvent(new CustomEvent('invalidCardPlay', {
            detail: {
                cardIndex: cardIndex
            }
        }));
        return; // Exit early, don't proceed with card play
    }
    // Card is valid, proceed with playing it
    // Play the card play sound
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('cardPlay');
    // Move card from hand to discard pile
    const playedCard = player.hand.splice(cardIndex, 1)[0];
    gameState.discardPile.push(playedCard);
    // Trim discard pile to keep only the 10 most recent cards when it exceeds 10 cards
    if (gameState.discardPile.length > 10) gameState.discardPile = gameState.discardPile.slice(-10);
    // Log the card play to console
    console.log(`${player.name} plays ${playedCard.color} ${playedCard.value} ${playedCard.emoji}`);
    // Update UI to show the played card
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)(gameState);
    // Check win condition IMMEDIATELY after playing the card
    // As soon as any player plays their last card, they win, regardless of card effects
    if (player.hand.length === 0) {
        // Store the winner and end the game immediately
        gameState.winningPlayerIndex = playerIndex;
        console.log(`${player.name} has played their last card and wins!`);
        $4ee2b06a9c1953f0$var$handleGameEnd();
        return;
    }
    // AI players need a pause after playing their card before continuing
    if (player.isAI) {
        // Add a 1 second pause for AI players after they play their card
        setTimeout(()=>{
            $4ee2b06a9c1953f0$export$9cce174fead5e557(playedCard, false, player, playerIndex);
        }, 1000);
        return;
    }
    // For human players, continue immediately
    $4ee2b06a9c1953f0$export$9cce174fead5e557(playedCard, false, player, playerIndex);
}
// Helper function to continue game flow after a card is played
function $4ee2b06a9c1953f0$export$9cce174fead5e557(playedCard, skipNextTurn, player, playerIndex) {
    // Special handling for wild cards played by human player
    if ((playedCard.value === 'Wild' || playedCard.value === 'Wild Draw 4') && $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex === 0) {
        // Set waiting for color choice flag
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.waitingForColorChoice = true;
        // For Wild Draw 4, set up the pending draw action for the next player
        if (playedCard.value === 'Wild Draw 4') {
            // Store the next player index who will draw
            $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex = $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame();
            $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawCount = 4;
        }
        // Show color choice UI
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
        return;
    } else {
        // Handle all other types of cards
        const specialCardDependencies = {
            getNextPlayerIndex: $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame,
            handleDrawCards: $4ee2b06a9c1953f0$var$handleDrawCards,
            handleSkipNextPlayer: $4ee2b06a9c1953f0$var$handleSkipNextPlayer,
            updateGameDisplay: $84923368ea4a1ab4$export$3f43ff7bf5d42a53,
            playCard: $4ee2b06a9c1953f0$export$317a6377ae525e39,
            drawSingleCard: $4ee2b06a9c1953f0$var$drawSingleCard,
            nextTurn: $4ee2b06a9c1953f0$var$nextTurn
        };
        const cardSkipNextTurn = skipNextTurn || (0, $9e99b98ae16147e3$export$d5d0975972f6aabc)(playedCard, $4ee2b06a9c1953f0$export$8e361a1ffbebf172, specialCardDependencies);
        // Check for Uno - automatically call for everyone
        if (player.hand.length === 1 && !player.hasCalledUno) {
            player.hasCalledUno = true;
            // Play the UNO call sound
            (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('unoCall');
        }
        // Move to next player's turn only if the special card didn't already handle it
        if (!cardSkipNextTurn) $4ee2b06a9c1953f0$var$nextTurn();
        // Update the UI
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
    }
    // If it's AI's turn, let them play
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex !== 0) {
        const aiDependencies = {
            playCard: $4ee2b06a9c1953f0$export$317a6377ae525e39,
            drawSingleCard: $4ee2b06a9c1953f0$var$drawSingleCard,
            nextTurn: $4ee2b06a9c1953f0$var$nextTurn,
            updateGameDisplay: $84923368ea4a1ab4$export$3f43ff7bf5d42a53
        };
        setTimeout(()=>(0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673)($4ee2b06a9c1953f0$export$8e361a1ffbebf172, aiDependencies), 1000);
    }
}
// Helper functions for special card handling
// Helper function to get next player index using the utils function
function $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame() {
    return (0, $820877bea407f5fe$export$62dd595d6a764af5)($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length);
}
// Handle drawing cards for the next player
function $4ee2b06a9c1953f0$var$handleDrawCards(numCards) {
    const nextPlayerIndex = $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame();
    const nextPlayer = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[nextPlayerIndex];
    console.log(`handleDrawCards: ${numCards} cards for player ${nextPlayerIndex} (${nextPlayer.name})`);
    // Check if we're in an AI-to-AI drawing situation
    const isAItoAIDrawing = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].isAI && nextPlayer.isAI;
    // Set drawing cards flag (if not AI-to-AI)
    if (!isAItoAIDrawing) {
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isDrawingCards = true;
        console.log("Setting isDrawingCards to true");
    }
    // If the next player is AI, draw cards automatically
    if (nextPlayer.isAI) {
        console.log(`AI ${nextPlayer.name} is drawing ${numCards} cards automatically`);
        // Draw multiple cards for AI
        for(let i = 0; i < numCards; i++)$4ee2b06a9c1953f0$var$drawSingleCard(nextPlayerIndex);
        // Update UI to show the AI's new cards
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
        // Play a sound effect to indicate cards were drawn
        (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('cardPlay');
        // Log the AI drawing for debugging
        console.log(`AI ${nextPlayer.name} drew ${numCards} cards`);
        // Always make sure drawing flag is cleared for AI
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isDrawingCards = false;
        // For AI-to-AI interactions, add a small delay to ensure UI updates properly
        if (isAItoAIDrawing) // Force a small delay to ensure the UI updates before continuing
        return nextPlayerIndex;
    } else {
        // For human player, set a draw requirement state
        console.log(`Setting requiredDraws=${numCards} for human player`);
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws = numCards;
        // Save the original player index - ONLY if we're not already handling a draw
        // This prevents overwriting pendingDrawPlayerIndex if already set
        if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex === null) {
            // Remember the actual current player index to restore it later
            $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex;
            console.log(`Saving currentPlayerIndex ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex} to pendingDrawPlayerIndex`);
            // Temporarily set current player to human player
            $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = 0;
            console.log("Setting currentPlayerIndex to 0 (human) temporarily");
        }
        // Update the UI to show the drawing state
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
        // Show a message indicating the player needs to draw cards and will lose their turn
        window.dispatchEvent(new CustomEvent('showDrawRequirement', {
            detail: {
                numCards: numCards,
                message: `Draw ${numCards} cards! Your turn will be skipped.`
            }
        }));
    // Note: For human players, the isDrawingCards flag will be cleared in handleRequiredDraw
    // when they finish drawing all required cards.
    // Also, handleRequiredDraw will advance to the next player once drawing is complete.
    }
    return nextPlayerIndex;
}
// Handle skipping the next player's turn
function $4ee2b06a9c1953f0$var$handleSkipNextPlayer(nextPlayerIndex) {
    // Skip their turn - in 2-player games this means it's your turn again
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length === 2) ;
    else {
        // For 3+ player games, skip the next player's turn
        // We need to move forward one more position in the current direction
        // without changing the direction itself
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = (nextPlayerIndex + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction) % $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
        if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex < 0) $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex += $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    }
}
// Handle wild card color selection
function $4ee2b06a9c1953f0$var$handleWildCardColor() {
    // For AI player, choose color based on hand
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex !== 0) {
        // AI chooses most frequent color in hand
        const player = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex];
        const chosenColor = (0, $c80abe9c5b3e3d60$export$1847a034464ce353)(player.hand);
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentColor = chosenColor;
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.waitingForColorChoice = false;
    }
// For human player, we'll show color choice UI (handled in playCard function)
}
// Show color choice UI for human player
function $4ee2b06a9c1953f0$var$showColorChoiceUI() {
    // This is handled directly in UI.js when gameState.waitingForColorChoice is true
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.waitingForColorChoice = true;
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
}
// Human player chooses color after playing a wild card
function $4ee2b06a9c1953f0$export$3d51a2be9557512a(gameStateParam, color) {
    // For compatibility - if only one parameter passed, it's the color
    if (color === undefined && typeof gameStateParam === 'string') {
        color = gameStateParam;
        gameStateParam = $4ee2b06a9c1953f0$export$8e361a1ffbebf172;
    }
    // Use either passed gameState or global gameState
    const gs = gameStateParam || $4ee2b06a9c1953f0$export$8e361a1ffbebf172;
    gs.currentColor = color;
    gs.waitingForColorChoice = false;
    // Get the last played card
    const lastPlayedCard = gs.discardPile[gs.discardPile.length - 1];
    const wasWildDraw4 = lastPlayedCard.value === 'Wild Draw 4';
    const aiDependencies = (0, $e04d9f482a55f400$export$8f1857c910e27042)($4ee2b06a9c1953f0$export$317a6377ae525e39, $4ee2b06a9c1953f0$var$drawSingleCard, $4ee2b06a9c1953f0$var$nextTurn, (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53));
    if (wasWildDraw4) $4ee2b06a9c1953f0$var$handleWildDraw4ColorChoice(gs, aiDependencies);
    else (0, $e04d9f482a55f400$export$2f7cb009dbcbf6ab)(gs, $4ee2b06a9c1953f0$var$nextTurn, (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53), (0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673), aiDependencies);
}
// Helper function to handle Wild Draw 4 color choice logic
function $4ee2b06a9c1953f0$var$handleWildDraw4ColorChoice(gs, aiDependencies) {
    // Store the next player index who will draw (if not already stored)
    if (gs.pendingDrawPlayerIndex === null) gs.pendingDrawPlayerIndex = $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame();
    // Get the next player who will draw
    const nextPlayerIndex = $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame();
    console.log(`After Wild Draw 4 color choice, next player (${gs.players[nextPlayerIndex].name}) will draw 4 cards`);
    // Have the next player draw 4 cards
    $4ee2b06a9c1953f0$var$handleDrawCards(4);
    // Handle AI and human players differently
    const isNextPlayerAI = gs.players[nextPlayerIndex].isAI;
    if (isNextPlayerAI) (0, $e04d9f482a55f400$export$a0fd2a2b4a7af6f)(gs, nextPlayerIndex, $4ee2b06a9c1953f0$var$handleSkipNextPlayer, (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53), (0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673), aiDependencies);
    else console.log("Human player needs to draw 4 cards from Wild Draw 4");
}
// Draw a single card and add it to player's hand
function $4ee2b06a9c1953f0$var$drawSingleCard(playerIndex) {
    // Check if deck is empty
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck.length === 0) $4ee2b06a9c1953f0$var$reshuffleDeck();
    // Log who we are drawing for
    console.log(`drawSingleCard for player: ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[playerIndex].name}`);
    // Draw a card from the deck
    const drawnCard = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck.pop();
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[playerIndex].hand.push(drawnCard);
    // Log the card draw to console
    const playerName = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[playerIndex].name;
    console.log(`${playerName} draws a ${drawnCard.color} ${drawnCard.value} ${drawnCard.emoji}`);
    return drawnCard;
}
// Animate the deck for a card draw effect
function $4ee2b06a9c1953f0$var$animateDeckDraw() {
    const deck = document.getElementById('deck');
    if (deck) // Quick animation to show the card is being drawn
    deck.animate([
        {
            transform: 'translateY(0) rotate(0deg)'
        },
        {
            transform: 'translateY(-20px) rotate(-5deg)'
        },
        {
            transform: 'translateY(0) rotate(0deg)'
        }
    ], {
        duration: 400,
        easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
    });
}
/**
 * Restores the player index after required draws and calculates skip target
 * @returns {number} The index of the player who should take the next turn
 */ function $4ee2b06a9c1953f0$var$restorePlayerIndexAndCalculateSkip() {
    console.log(`Before index restoration - currentPlayerIndex: ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex}, pendingDrawPlayerIndex: ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex}`);
    // Restore the original player index if we temporarily changed it
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex !== null && $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex !== undefined) {
        console.log(`Restoring player index from ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex} to ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex}`);
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex;
        $4ee2b06a9c1953f0$export$8e361a1ffbebf172.pendingDrawPlayerIndex = null;
    } else console.log("No player index to restore (pendingDrawPlayerIndex was null)");
    // Calculate the player after the human who should take the next turn
    const humanPlayerIndex = 0;
    let nextAfterHumanIndex = (humanPlayerIndex + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction) % $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    if (nextAfterHumanIndex < 0) nextAfterHumanIndex += $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    // Ensure we never give the human player an immediate turn after drawing
    if (nextAfterHumanIndex === 0) {
        nextAfterHumanIndex = (nextAfterHumanIndex + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction) % $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
        if (nextAfterHumanIndex < 0) nextAfterHumanIndex += $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    }
    return nextAfterHumanIndex;
}
/**
 * Completes the required draw sequence by setting up the next turn
 * @param {number} nextPlayerIndex - Index of the player who should take the next turn
 */ function $4ee2b06a9c1953f0$var$completeRequiredDrawSequence(nextPlayerIndex) {
    // Play sound and reset drawing state
    setTimeout(()=>(0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('cardPlay'), 300);
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isDrawingCards = false;
    // Set up turn skip notification
    const nextPlayerIndex_forUI = $4ee2b06a9c1953f0$var$getNextPlayerIndexForGame();
    const skipToPlayerIndex = (nextPlayerIndex_forUI + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction) % $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    const skipToPlayerName = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[skipToPlayerIndex < 0 ? skipToPlayerIndex + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length : skipToPlayerIndex].name;
    window.dispatchEvent(new CustomEvent('drawRequirementComplete', {
        detail: {
            message: `Cards drawn! Your turn is skipped. ${skipToPlayerName}'s turn now.`
        }
    }));
    // Set the next player
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = nextPlayerIndex;
    console.log("After Draw 2/Draw 4 turn skip - Current player is now:", $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex, `(${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name})`);
    // Start the next AI turn after a delay
    setTimeout(()=>{
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
        // Safety check and start AI turn
        if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex === 0) {
            console.error("ERROR: Human player should not get turn immediately after drawing from Draw 2/Draw 4");
            $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = 1;
        }
        console.log(`Starting AI turn for ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name} after human finished drawing`);
        const aiDependencies = (0, $e04d9f482a55f400$export$8f1857c910e27042)($4ee2b06a9c1953f0$export$317a6377ae525e39, $4ee2b06a9c1953f0$var$drawSingleCard, $4ee2b06a9c1953f0$var$nextTurn, (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53));
        setTimeout(()=>(0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673)($4ee2b06a9c1953f0$export$8e361a1ffbebf172, aiDependencies), 1000);
    }, 1000);
}
// Handle required draws (Draw 2 or Draw 4)
function $4ee2b06a9c1953f0$var$handleRequiredDraw() {
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws--;
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws === 0) {
        const nextPlayerIndex = $4ee2b06a9c1953f0$var$restorePlayerIndexAndCalculateSkip();
        $4ee2b06a9c1953f0$var$completeRequiredDrawSequence(nextPlayerIndex);
        return true; // Handled the last required draw
    }
    return false; // More required draws needed
}
// Player draws a card
function $4ee2b06a9c1953f0$export$ebab430dd53d463d() {
    console.log(`drawCard called - requiredDraws: ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws}`);
    // Validate if draw is allowed
    const validation = (0, $e04d9f482a55f400$export$812129311cdd0390)($4ee2b06a9c1953f0$export$8e361a1ffbebf172, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.waitingForColorChoice);
    if (!validation.isValid) {
        if (validation.reason === 'not_human_turn' || validation.reason === 'waiting_for_color') return;
    }
    // Check if player has legal moves and there are no required draws
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws === 0) {
        const topDiscard = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile.length - 1];
        if ((0, $c80abe9c5b3e3d60$export$c7fe003a8301344f)($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[0].hand, topDiscard, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentColor)) {
            // Trigger shake animation on the deck to indicate player should play a card
            window.dispatchEvent(new CustomEvent('invalidDraw'));
            return; // Don't allow drawing if player has legal moves
        }
    }
    // Play a soft "draw" sound
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('cardPlay');
    // Visual feedback - animate the deck
    $4ee2b06a9c1953f0$var$animateDeckDraw();
    // Determine which player should be drawing the cards
    // If there are required draws, the cards should go to the human player (index 0)
    // regardless of who the current player is
    const playerIndexForDraw = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws > 0 ? 0 : $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex;
    // Draw ONE CARD AT A TIME - always just one card per click
    const drawnCard = $4ee2b06a9c1953f0$var$drawSingleCard(playerIndexForDraw);
    // Handle different draw scenarios
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws > 0) $4ee2b06a9c1953f0$var$handleRequiredCardDraw(playerIndexForDraw, drawnCard);
    else $4ee2b06a9c1953f0$var$handleNormalCardDraw(drawnCard);
}
// Handle drawing cards when required (Draw 2 or Draw 4)
function $4ee2b06a9c1953f0$var$handleRequiredCardDraw(playerIndexForDraw, drawnCard) {
    // Calculate how many cards were originally required (before this draw)
    const originalRequiredDraws = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws + 1;
    // Calculate which card we're drawing in the sequence (for clear logging)
    const currentDrawNumber = originalRequiredDraws - $4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws;
    console.log(`Drew card ${currentDrawNumber} of ${originalRequiredDraws} required draws for player ${playerIndexForDraw} (${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[playerIndexForDraw].name})`);
    const turnComplete = $4ee2b06a9c1953f0$var$handleRequiredDraw();
    if (!turnComplete) {
        console.log(`Still need to draw ${$4ee2b06a9c1953f0$export$8e361a1ffbebf172.requiredDraws} more cards`);
        // Return after drawing a required card - player needs to click again for next card
        return;
    }
    console.log("All required cards drawn - turn complete");
}
// Handle normal card drawing (not required)
function $4ee2b06a9c1953f0$var$handleNormalCardDraw(drawnCard) {
    console.log("Normal draw (not required) - drew one card");
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
    const topDiscard = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile.length - 1];
    const player = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex];
    const cardIsPlayable = (0, $c80abe9c5b3e3d60$export$c7fc79ab8aa6fc62)(drawnCard, topDiscard, $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentColor, player.hand);
    if (!cardIsPlayable) {
        console.log("Drawn card can't be played - moving to next player");
        // Move to next player's turn
        $4ee2b06a9c1953f0$var$nextTurn();
        (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
        // If it's AI's turn, let them play
        const aiDependencies = (0, $e04d9f482a55f400$export$8f1857c910e27042)($4ee2b06a9c1953f0$export$317a6377ae525e39, $4ee2b06a9c1953f0$var$drawSingleCard, $4ee2b06a9c1953f0$var$nextTurn, (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53));
        (0, $e04d9f482a55f400$export$4e804bd8a20f2dcb)($4ee2b06a9c1953f0$export$8e361a1ffbebf172, (0, $a9aa67ee0388acd1$export$b5e0e64c49f4a673), aiDependencies, "after human draw");
    } else console.log("Drawn card can be played - waiting for player to play it");
}
// Move to next player's turn
function $4ee2b06a9c1953f0$var$nextTurn() {
    const previousPlayerIndex = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex;
    const previousPlayerName = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[previousPlayerIndex].name;
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex = ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex + $4ee2b06a9c1953f0$export$8e361a1ffbebf172.direction) % $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex < 0) $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex += $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players.length;
    const nextPlayerName = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name;
    console.log(`Turn: ${previousPlayerName} \u{2192} ${nextPlayerName}`);
    // Play your turn sound if it's the player's turn
    if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex === 0) (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('yourTurn');
    else if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name === 'Bingo') (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('bingo');
    else if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name === 'Bluey') (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('bluey');
    else if ($4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[$4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex].name === 'Dad') (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('dad');
}
// Reshuffle discard pile into deck
function $4ee2b06a9c1953f0$var$reshuffleDeck() {
    // Keep the top card of the discard pile
    const topCard = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile.pop();
    // Move all other cards to the deck and shuffle
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.deck = (0, $125bb8bfc3b37dfd$export$2948b794f685d50b)([
        ...$4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile
    ]);
    // Clear discard pile and put top card back
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.discardPile = [
        topCard
    ];
}
// Player says Uno
function $4ee2b06a9c1953f0$export$bcc78e7920d2249f() {
    const player = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[0]; // Human player
    player.hasCalledUno = true;
    // Play the UNO call sound
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('unoCall');
    // Update UI
    (0, $84923368ea4a1ab4$export$3f43ff7bf5d42a53)($4ee2b06a9c1953f0$export$8e361a1ffbebf172);
}
// Handle game end
function $4ee2b06a9c1953f0$var$handleGameEnd() {
    // Use the stored winning player index if available, otherwise use current player
    const winnerIndex = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.winningPlayerIndex !== undefined ? $4ee2b06a9c1953f0$export$8e361a1ffbebf172.winningPlayerIndex : $4ee2b06a9c1953f0$export$8e361a1ffbebf172.currentPlayerIndex;
    const winnerName = $4ee2b06a9c1953f0$export$8e361a1ffbebf172.players[winnerIndex].name;
    const isPlayerWinner = winnerIndex === 0;
    // Play victory sound
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).play('win');
    // Create a fun victory screen
    const victoryScreen = document.createElement('div');
    victoryScreen.style.position = 'fixed';
    victoryScreen.style.top = '0';
    victoryScreen.style.left = '0';
    victoryScreen.style.width = '100%';
    victoryScreen.style.height = '100%';
    victoryScreen.style.backgroundColor = isPlayerWinner ? 'rgba(255, 215, 0, 0.8)' : 'rgba(100, 149, 237, 0.8)';
    victoryScreen.style.display = 'flex';
    victoryScreen.style.flexDirection = 'column';
    victoryScreen.style.justifyContent = 'center';
    victoryScreen.style.alignItems = 'center';
    victoryScreen.style.zIndex = '2000';
    // Winner announcement
    const winnerMessage = document.createElement('h1');
    winnerMessage.style.fontSize = '40px';
    winnerMessage.style.color = 'white';
    winnerMessage.style.textShadow = '0 0 10px rgba(0, 0, 0, 0.5)';
    winnerMessage.style.marginBottom = '20px';
    winnerMessage.style.textAlign = 'center';
    winnerMessage.style.padding = '0 20px';
    // Add emojis and different messages based on who won
    if (isPlayerWinner) winnerMessage.innerHTML = "\uD83C\uDF89 YOU WIN! \uD83C\uDF89<br>HOORAY!";
    else winnerMessage.innerHTML = `${winnerName} wins!<br>Try again!`;
    // Add celebration emojis that float up
    for(let i = 0; i < 20; i++){
        const emoji = document.createElement('div');
        emoji.style.position = 'absolute';
        emoji.style.fontSize = '30px';
        emoji.style.left = `${Math.random() * 100}%`;
        emoji.style.bottom = '0';
        emoji.style.opacity = '0';
        emoji.textContent = [
            "\uD83C\uDF89",
            "\uD83C\uDF8A",
            "\uD83C\uDF88",
            "\u2B50",
            "\uD83C\uDFC6"
        ][Math.floor(Math.random() * 5)];
        // Animate the emoji floating up
        emoji.animate([
            {
                transform: 'translateY(0)',
                opacity: 1
            },
            {
                transform: `translateY(-${Math.random() * 400 + 200}px)`,
                opacity: 0
            }
        ], {
            duration: Math.random() * 3000 + 2000,
            iterations: Infinity
        });
        victoryScreen.appendChild(emoji);
    }
    // Play again button
    const playAgainButton = document.createElement('button');
    playAgainButton.textContent = 'Play Again';
    playAgainButton.style.fontSize = '24px';
    playAgainButton.style.padding = '15px 30px';
    playAgainButton.style.marginTop = '30px';
    playAgainButton.style.backgroundColor = '#4CAF50';
    playAgainButton.style.border = 'none';
    playAgainButton.style.borderRadius = '50px';
    playAgainButton.style.color = 'white';
    playAgainButton.style.cursor = 'pointer';
    playAgainButton.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
    playAgainButton.innerHTML = "\u25B6\uFE0F Play Again \u25B6\uFE0F";
    playAgainButton.addEventListener('click', ()=>{
        document.body.removeChild(victoryScreen);
        // Show welcome screen instead of directly starting a new game
        (0, $84923368ea4a1ab4$export$cea7e42d5654180f)();
    });
    victoryScreen.appendChild(winnerMessage);
    victoryScreen.appendChild(playAgainButton);
    document.body.appendChild(victoryScreen);
    // Game is ended - we'll let them tap on deck to start a new game later
    // Reset game state
    $4ee2b06a9c1953f0$export$8e361a1ffbebf172.isGameStarted = false;
}
// Toggle sound
function $4ee2b06a9c1953f0$var$toggleSound() {
    const button = document.getElementById('toggle-sound');
    const isMuted = button.classList.toggle('muted');
    (0, $b7cfe35c464a2c84$export$2e2bcd8739ae039).toggleSound(!isMuted);
}


//# sourceMappingURL=repo.44bd74b1.js.map
